import React from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "../api";
import { notifyWorkspaceChanged, subscribeToWorkspaceChanged } from "../workspaceSync";

type Status = "green" | "green_strong" | "yellow" | "red" | "neutral";
type Campus = string;
type Frequency = "weekly" | "monthly" | "bimonthly" | "quarterly" | "yearly" | "periodic";
type WeekStartsOn = "saturday" | "sunday" | "monday";
type Scope = "campus" | "global";
type PerformanceDirection = "higher_is_better" | "lower_is_better";
type ValueType = "count" | "currency" | "percentage" | "rating_1_10";
type MetricPersistence = "period_only" | "carry_forward";
type AggregationMode = "current" | "mtd" | "qtd" | "ytd";
type ComparisonMode = "target" | "previous_period" | "same_period_last_year";
type KpiCalculationMode =
  | "direct"
  | "calculated_values"
  | "influenced_by_children"
  | "formula_percentage";
type WorkStatus = "not-started" | "in-progress" | "blocked" | "done";
type WorkPriority = "low" | "normal" | "high" | "critical";
type WorkEffort = "light" | "medium" | "heavy";

type SignedInUser = {
  id: number;
  name: string;
  email: string;
  role: string;
  enterprise_id?: number | null;
  is_active?: boolean;
};

type EnterpriseSummary = {
  id: number;
  name: string;
  slug: string;
  week_starts_on?: WeekStartsOn | null;
  updated_at?: string;
};

type PollQuestionType = "number" | "rating" | "yes_no" | "text";
type PollResponseValue = number | boolean | string;
type PollResponse = {
  id: string;
  submittedAt: string;
  respondentLabel?: string;
  respondentGroup?: string;
  value: PollResponseValue;
  comment?: string;
};
type PollInvite = {
  id: string;
  token: string;
  recipientId: string;
  recipientName: string;
  recipientEmail: string;
  sentAt?: string;
  completedAt?: string;
};
type PollRecipient = {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  groups: string[];
  name?: string;
};
type PollConfig = {
  id: string;
  campaignRootKpiId?: string;
  linkedKpiIds: string[];
  title: string;
  description: string;
  targetGroup?: string;
  targetGroups: string[];
  questionPrompt: string;
  questionType: PollQuestionType;
  isAnonymous: boolean;
  status: "draft" | "published";
  publishedAt?: string;
  responses: PollResponse[];
  invites: PollInvite[];
};

type TaskItem = {
  id: string;
  title: string;
  owner: string;
  dueDate: string;
  status: WorkStatus;
  priority?: WorkPriority;
  effort?: WorkEffort;
  notes: string;
};

type InitiativeItem = {
  id: string;
  title: string;
  owner: string;
  dueDate: string;
  status: WorkStatus;
  priority?: WorkPriority;
  effort?: WorkEffort;
  summary: string;
  tasks: TaskItem[];
};

type LensConfig = {
  name: string;
  scopeType: string;
  scopeId: string;
  description: string;
};

type LensConfigs = Record<string, LensConfig>;
type MetricHistory = Record<string, number | undefined>;
type RestrictedFields = Record<string, string[]>;

type PersistedNodeData = {
  label: string;
  targetHistory: MetricHistory;
  actualHistory: MetricHistory;
  unit?: string;
  frequency: Frequency;
  scope: Scope;
  performanceDirection: PerformanceDirection;
  metricPersistence?: MetricPersistence;
  calculationMode?: KpiCalculationMode;
  formulaNumeratorKpiId?: string;
  formulaDenominatorKpiId?: string;
  valueType?: ValueType;
  eventDate?: string;
  definition?: string;
  measurement?: string;
  updateNotes?: string;
  owner?: string;
  dataSource?: string;
  pollConfig?: PollConfig | null;
  restrictedFields?: RestrictedFields;
  metricsRestricted?: boolean;
  status?: Status;
  colorActual?: number;
  lenses?: string[];
  isRollup?: boolean;
  initiatives?: InitiativeItem[];
  tasks?: TaskItem[];
};

type PersistedNode = {
  id: string;
  data: PersistedNodeData;
};

type PersistedEdge = {
  id: string;
  source: string;
  target: string;
  data?: {
    influence?: number;
  };
};

type DashboardConfig = {
  trackedKpiIds: string[];
  trackedKpiLabels?: string[];
};

type EnterpriseIdentity = {
  id: number | null;
  slug: string;
  name: string;
};

type Workspace = {
  enterpriseIdentity?: EnterpriseIdentity;
  availableLenses?: string[];
  lensConfigs?: LensConfigs;
  activeLens?: string;
  referenceDate?: string;
  aggregationMode?: AggregationMode;
  comparisonMode?: ComparisonMode;
  ownerList?: string[];
  pollAudienceGroups?: string[];
  pollRecipients?: PollRecipient[];
  allNodes?: PersistedNode[];
  allEdges?: PersistedEdge[];
  dashboardConfigs?: Record<string, DashboardConfig>;
};

type DashboardNode = {
  id: string;
  label: string;
  owner: string;
  target?: number;
  actual?: number;
  frequency: Frequency;
  valueType: ValueType;
  unit: string;
  performanceDirection: PerformanceDirection;
  calculationMode: KpiCalculationMode;
  status: Status;
  statusNote?: string;
  colorActual?: number;
  metricsRestricted?: boolean;
  actualHistory: MetricHistory;
};

type DashboardDisplayCard = {
  node: DashboardNode;
  depth: number;
  childCount: number;
  hasTrackedParent: boolean;
  parentId?: string;
  parentLabel?: string;
  isLastChild?: boolean;
};
type DashboardInsertPosition = "before" | "after";

const activeEnterpriseStorageKey = "lenses.activeEnterpriseId";
const activeDashboardOwnerStorageKey = "lenses.activeDashboardOwner";
const workspaceCacheVersion = "postgres-v2";
const getEnterpriseCacheIdentity = (enterprise: EnterpriseSummary | null | undefined) => {
  const rawIdentity = enterprise?.slug || enterprise?.name || "unknown";
  return rawIdentity
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "") || "unknown";
};
const getWorkspaceEnterpriseIdentity = (
  enterpriseId: number | null,
  enterprise: EnterpriseSummary | null | undefined
): EnterpriseIdentity => ({
  id: enterpriseId,
  slug: enterprise?.slug ?? "",
  name: enterprise?.name ?? "",
});
const getWorkspaceCacheKey = (enterpriseId: number, enterprise: EnterpriseSummary | null | undefined) =>
  `lenses.workspaceCache.${workspaceCacheVersion}.${enterpriseId}.${getEnterpriseCacheIdentity(enterprise)}`;
const defaultCampus: Campus = "global";
const defaultWeekStartsOn: WeekStartsOn = "monday";
const normalizeWeekStartsOn = (value: unknown): WeekStartsOn =>
  value === "saturday" || value === "sunday" || value === "monday" ? value : defaultWeekStartsOn;
const getReportingWeekStart = (date: Date, weekStartsOn: WeekStartsOn) => {
  const nextDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const day = nextDate.getDay();
  const targetDay = weekStartsOn === "saturday" ? 6 : weekStartsOn === "sunday" ? 0 : 1;
  const offset = (day - targetDay + 7) % 7;
  nextDate.setDate(nextDate.getDate() - offset);
  return nextDate;
};
const getInitialReferenceDate = (weekStartsOn: WeekStartsOn = defaultWeekStartsOn) =>
  getReportingWeekStart(new Date(), weekStartsOn);
const initialReferenceDate = getInitialReferenceDate();
const brandLogoUrl = `${process.env.PUBLIC_URL}/favicon.png?v=2`;

const valueTypeOptions: ValueType[] = ["count", "currency", "percentage", "rating_1_10"];
const dataSourceOptions = ["Manual", "API", "Poll"] as const;
const frequencyOptions: Frequency[] = ["weekly", "monthly", "bimonthly", "quarterly", "yearly", "periodic"];
const calculationModeOptions: KpiCalculationMode[] = [
  "direct",
  "calculated_values",
  "influenced_by_children",
  "formula_percentage",
];
const comparisonModeOptions: ComparisonMode[] = ["target", "previous_period", "same_period_last_year"];
const workStatusOptions: WorkStatus[] = ["not-started", "in-progress", "blocked", "done"];
const workPriorityOptions: WorkPriority[] = ["low", "normal", "high", "critical"];
const workEffortOptions: WorkEffort[] = ["light", "medium", "heavy"];

const toTitleCase = (value: string) => value.charAt(0).toUpperCase() + value.slice(1);
const formatEnumLabel = (value: string) =>
  value
    .split("_")
    .map((part) => toTitleCase(part))
    .join(" ");
const formatValueTypeLabel = (value: ValueType) =>
  value === "rating_1_10" ? "Rating (1-10)" : formatEnumLabel(value);
const formatWorkStatusLabel = (status: WorkStatus) => formatEnumLabel(status.replace("-", "_"));
const formatWorkPriorityLabel = (priority: WorkPriority | undefined) =>
  formatEnumLabel(priority ?? "normal");
const formatWorkEffortLabel = (effort: WorkEffort | undefined) => formatEnumLabel(effort ?? "medium");
const formatWorkDueDate = (value: string) => {
  if (!value) return "No due date";
  const parsed = new Date(`${value}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return "No due date";
  return `Due ${parsed.toLocaleDateString(undefined, { month: "short", day: "numeric" })}`;
};
const workStatusChipStyle = (status: WorkStatus): React.CSSProperties => {
  const palette: Record<WorkStatus, { background: string; color: string; border: string }> = {
    "not-started": { background: "#f3f4f6", color: "#374151", border: "#e5e7eb" },
    "in-progress": { background: "#eff6ff", color: "#1d4ed8", border: "#bfdbfe" },
    blocked: { background: "#fff1f2", color: "#be123c", border: "#fecdd3" },
    done: { background: "#ecfdf5", color: "#047857", border: "#a7f3d0" },
  };
  const colors = palette[status];
  return {
    display: "inline-flex",
    alignItems: "center",
    minHeight: 22,
    border: `1px solid ${colors.border}`,
    borderRadius: 999,
    padding: "2px 8px",
    background: colors.background,
    color: colors.color,
    fontSize: 12,
    fontWeight: 800,
    lineHeight: 1.2,
  };
};
const workPriorityChipStyle = (priority: WorkPriority | undefined): React.CSSProperties => {
  const palette: Record<WorkPriority, { background: string; color: string; border: string }> = {
    low: { background: "#f8fafc", color: "#64748b", border: "#e2e8f0" },
    normal: { background: "#f3f4f6", color: "#374151", border: "#e5e7eb" },
    high: { background: "#fff7ed", color: "#c2410c", border: "#fed7aa" },
    critical: { background: "#fef2f2", color: "#b91c1c", border: "#fecaca" },
  };
  const colors = palette[priority ?? "normal"];
  return {
    ...workStatusChipStyle("not-started"),
    border: `1px solid ${colors.border}`,
    background: colors.background,
    color: colors.color,
  };
};
const workEffortChipStyle = (effort: WorkEffort | undefined): React.CSSProperties => {
  const palette: Record<WorkEffort, { background: string; color: string; border: string }> = {
    light: { background: "#f0fdf4", color: "#15803d", border: "#bbf7d0" },
    medium: { background: "#f8fafc", color: "#475569", border: "#e2e8f0" },
    heavy: { background: "#f5f3ff", color: "#6d28d9", border: "#ddd6fe" },
  };
  const colors = palette[effort ?? "medium"];
  return {
    ...workStatusChipStyle("not-started"),
    border: `1px solid ${colors.border}`,
    background: colors.background,
    color: colors.color,
  };
};
const clampRatingValue = (value: number) => Math.min(10, Math.max(1, value));

const createWorkId = () =>
  typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

const createTask = (): TaskItem => ({
  id: createWorkId(),
  title: "New task",
  owner: "",
  dueDate: "",
  status: "not-started",
  priority: "normal",
  effort: "medium",
  notes: "",
});

const createInitiative = (): InitiativeItem => ({
  id: createWorkId(),
  title: "New initiative",
  owner: "",
  dueDate: "",
  status: "not-started",
  priority: "normal",
  effort: "medium",
  summary: "",
  tasks: [],
});

const createPollConfig = (kpi?: { label?: string; definition?: string; measurement?: string }): PollConfig => ({
  id: createWorkId(),
  linkedKpiIds: [],
  title: `${kpi?.label ?? "KPI"} Poll`,
  description: "",
  targetGroups: [],
  questionPrompt:
    kpi?.measurement || kpi?.definition
      ? `How would you rate ${kpi?.label ?? "this KPI"} based on ${kpi.measurement || kpi.definition}?`
      : `How would you rate ${kpi?.label ?? "this KPI"} on a 1-10 scale?`,
  questionType: "rating",
  isAnonymous: true,
  status: "draft",
  responses: [],
  invites: [],
});

const getNormalizedValueType = (dataSource: string | undefined, valueType: unknown): ValueType => {
  if (dataSource === "Poll") return "rating_1_10";
  return typeof valueType === "string" && valueTypeOptions.includes(valueType as ValueType)
    ? (valueType as ValueType)
    : "count";
};

const normalizeCalculationMode = (
  value: unknown,
  fallbackIsRollup = false
): KpiCalculationMode => {
  if (
    value === "calculated_values" ||
    value === "influenced_by_children" ||
    value === "formula_percentage" ||
    value === "direct"
  ) {
    return value;
  }
  return fallbackIsRollup ? "influenced_by_children" : "direct";
};
const formatCalculationModeLabel = (mode: KpiCalculationMode) => {
  switch (mode) {
    case "calculated_values":
      return "Calculated from child values";
    case "influenced_by_children":
      return "Influenced by child performance";
    case "formula_percentage":
      return "Calculated percentage from KPIs";
    default:
      return "Direct KPI";
  }
};

const calculateFormulaPercentage = (
  numerator?: number,
  denominator?: number
) => {
  if (
    typeof numerator !== "number" ||
    Number.isNaN(numerator) ||
    typeof denominator !== "number" ||
    Number.isNaN(denominator) ||
    denominator <= 0
  ) {
    return undefined;
  }
  return (numerator / denominator) * 100;
};

const normalizePollConfig = (
  pollConfig?: (Partial<PollConfig> & { status?: string }) | null
): PollConfig | null => {
  if (!pollConfig) return null;
  const rawStatus = (pollConfig as { status?: string }).status;
  return {
    ...pollConfig,
    id: pollConfig.id ?? `${Math.random().toString(36).slice(2, 10)}${Date.now().toString(36)}`,
    campaignRootKpiId:
      typeof pollConfig.campaignRootKpiId === "string" && pollConfig.campaignRootKpiId.trim().length > 0
        ? pollConfig.campaignRootKpiId
        : undefined,
    linkedKpiIds: Array.isArray(pollConfig.linkedKpiIds)
      ? pollConfig.linkedKpiIds.filter((value): value is string => typeof value === "string")
      : [],
    title: pollConfig.title ?? "New KPI Poll",
    description: pollConfig.description ?? "",
    questionPrompt: pollConfig.questionPrompt ?? "How would you rate this KPI on a 1-10 scale?",
    questionType: pollConfig.questionType ?? "rating",
    isAnonymous: pollConfig.isAnonymous ?? true,
    responses: Array.isArray(pollConfig.responses) ? pollConfig.responses.map((item) => ({ ...item })) : [],
    invites: Array.isArray(pollConfig.invites) ? pollConfig.invites.map((item) => ({ ...item })) : [],
    targetGroups:
      Array.isArray(pollConfig.targetGroups) && pollConfig.targetGroups.length > 0
        ? pollConfig.targetGroups
        : pollConfig.targetGroup
          ? [pollConfig.targetGroup]
          : [],
    targetGroup: undefined,
    status: rawStatus === "published" || rawStatus === "active" ? "published" : "draft",
    publishedAt: pollConfig.publishedAt,
  };
};

const derivePollActualHistory = (
  pollConfig: PollConfig | null | undefined,
  frequency: Frequency,
  scope: Scope,
  campus: Campus
): MetricHistory => {
  if (!pollConfig) return {};
  const valuesByKey = new Map<string, number[]>();
  pollConfig.responses.forEach((response) => {
    const numericValue =
      typeof response.value === "number" ? response.value : Number(response.value);
    if (Number.isNaN(numericValue)) return;
    const submittedAt = new Date(response.submittedAt);
    if (Number.isNaN(submittedAt.getTime())) return;
    const key = `${getPeriodKey(submittedAt, frequency)}::${getScopeKey(scope, campus)}`;
    valuesByKey.set(key, [...(valuesByKey.get(key) ?? []), numericValue]);
  });
  return Object.fromEntries(
    Array.from(valuesByKey.entries()).map(([key, values]) => [
      key,
      values.reduce((sum, value) => sum + value, 0) / values.length,
    ])
  );
};

const normalizeWorkspace = (workspace?: Workspace | null): Workspace => {
  const nextWorkspace = workspace ?? {};
  return {
    ...nextWorkspace,
    dashboardConfigs:
      nextWorkspace.dashboardConfigs && typeof nextWorkspace.dashboardConfigs === "object"
        ? Object.fromEntries(
            Object.entries(nextWorkspace.dashboardConfigs).map(([owner, config]) => [
              owner,
              {
                trackedKpiIds: Array.isArray((config as DashboardConfig | undefined)?.trackedKpiIds)
                  ? (config as DashboardConfig).trackedKpiIds.filter(
                      (value): value is string => typeof value === "string"
                    )
                  : [],
                trackedKpiLabels: Array.isArray((config as DashboardConfig | undefined)?.trackedKpiLabels)
                  ? (config as DashboardConfig).trackedKpiLabels?.filter(
                      (value): value is string => typeof value === "string"
                    )
                  : [],
              } satisfies DashboardConfig,
            ])
          )
        : {},
    allNodes: (nextWorkspace.allNodes ?? []).map((node) => ({
      ...node,
      data: {
        ...node.data,
        unit: node.data.dataSource === "Poll" ? "" : node.data.unit ?? "",
        valueType: getNormalizedValueType(node.data.dataSource, node.data.valueType),
        formulaNumeratorKpiId: node.data.formulaNumeratorKpiId ?? "",
        formulaDenominatorKpiId: node.data.formulaDenominatorKpiId ?? "",
        calculationMode: normalizeCalculationMode(node.data.calculationMode, node.data.isRollup),
        pollConfig: normalizePollConfig(node.data.pollConfig),
        initiatives: (node.data.initiatives ?? []).map((initiative) => ({
          ...initiative,
          tasks: initiative.tasks ?? [],
        })),
        tasks: node.data.tasks ?? [],
        actualHistory:
          node.data.dataSource === "Poll"
            ? derivePollActualHistory(
                normalizePollConfig(node.data.pollConfig),
                node.data.frequency ?? "weekly",
                "global",
                defaultCampus
              )
            : node.data.actualHistory ?? {},
      },
    })),
    pollAudienceGroups: Array.isArray(nextWorkspace.pollAudienceGroups)
      ? nextWorkspace.pollAudienceGroups
      : [],
    pollRecipients: Array.isArray(nextWorkspace.pollRecipients)
      ? nextWorkspace.pollRecipients
      : [],
  };
};

const padTwo = (value: number) => String(value).padStart(2, "0");
const getIsoWeekParts = (date: Date) => {
  const utcDate = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const day = utcDate.getUTCDay() || 7;
  utcDate.setUTCDate(utcDate.getUTCDate() + 4 - day);
  const yearStart = new Date(Date.UTC(utcDate.getUTCFullYear(), 0, 1));
  const week = Math.ceil((((utcDate.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
  return { year: utcDate.getUTCFullYear(), week };
};
const getReportingWeekParts = (date: Date, weekStartsOn: WeekStartsOn) => {
  const weekStart = getReportingWeekStart(date, weekStartsOn);
  const weekNumberDate = new Date(weekStart);
  if (weekStartsOn === "saturday") {
    weekNumberDate.setDate(weekNumberDate.getDate() + 2);
  } else if (weekStartsOn === "sunday") {
    weekNumberDate.setDate(weekNumberDate.getDate() + 1);
  }
  return getIsoWeekParts(weekNumberDate);
};
const getDateFromIsoWeek = (year: number, week: number) => {
  const simple = new Date(Date.UTC(year, 0, 1 + (week - 1) * 7));
  const dayOfWeek = simple.getUTCDay() || 7;
  if (dayOfWeek <= 4) {
    simple.setUTCDate(simple.getUTCDate() - dayOfWeek + 1);
  } else {
    simple.setUTCDate(simple.getUTCDate() + 8 - dayOfWeek);
  }
  return new Date(simple.getUTCFullYear(), simple.getUTCMonth(), simple.getUTCDate());
};
const getPeriodKey = (date: Date, frequency: Frequency) => {
  if (frequency === "weekly") {
    const iso = getIsoWeekParts(date);
    return `${iso.year}-W${padTwo(iso.week)}`;
  }
  if (frequency === "monthly") return `${date.getFullYear()}-${padTwo(date.getMonth() + 1)}`;
  if (frequency === "bimonthly") return `${date.getFullYear()}-B${Math.floor(date.getMonth() / 2) + 1}`;
  if (frequency === "quarterly") return `${date.getFullYear()}-Q${Math.floor(date.getMonth() / 3) + 1}`;
  if (frequency === "periodic") {
    return `${date.getFullYear()}-${padTwo(date.getMonth() + 1)}-${padTwo(date.getDate())}`;
  }
  return `${date.getFullYear()}`;
};
const getScopeKey = (_scope: Scope, _campus: Campus) => "global";

const getStartOfDay = (date: Date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());
const getStartOfMonth = (date: Date) => new Date(date.getFullYear(), date.getMonth(), 1);
const getStartOfQuarter = (date: Date) =>
  new Date(date.getFullYear(), Math.floor(date.getMonth() / 3) * 3, 1);
const getStartOfYear = (date: Date) => new Date(date.getFullYear(), 0, 1);
const getAggregationStart = (date: Date, aggregationMode: AggregationMode) => {
  switch (aggregationMode) {
    case "mtd":
      return getStartOfMonth(date);
    case "qtd":
      return getStartOfQuarter(date);
    case "ytd":
      return getStartOfYear(date);
    default:
      return null;
  }
};
const getPeriodRange = (frequency: Frequency, periodKey: string) => {
  if (frequency === "weekly") {
    const match = /^(\d{4})-W(\d{2})$/.exec(periodKey);
    if (!match) return null;
    const start = getDateFromIsoWeek(Number(match[1]), Number(match[2]));
    const end = new Date(start);
    end.setDate(end.getDate() + 6);
    return { start, end };
  }
  if (frequency === "monthly") {
    const match = /^(\d{4})-(\d{2})$/.exec(periodKey);
    if (!match) return null;
    const start = new Date(Number(match[1]), Number(match[2]) - 1, 1);
    const end = new Date(Number(match[1]), Number(match[2]), 0);
    return { start, end };
  }
  if (frequency === "bimonthly") {
    const match = /^(\d{4})-B([1-6])$/.exec(periodKey);
    if (!match) return null;
    const startMonth = (Number(match[2]) - 1) * 2;
    return {
      start: new Date(Number(match[1]), startMonth, 1),
      end: new Date(Number(match[1]), startMonth + 2, 0),
    };
  }
  if (frequency === "quarterly") {
    const match = /^(\d{4})-Q([1-4])$/.exec(periodKey);
    if (!match) return null;
    const startMonth = (Number(match[2]) - 1) * 3;
    return {
      start: new Date(Number(match[1]), startMonth, 1),
      end: new Date(Number(match[1]), startMonth + 3, 0),
    };
  }
  if (frequency === "yearly") {
    const year = Number(periodKey);
    if (Number.isNaN(year)) return null;
    return { start: new Date(year, 0, 1), end: new Date(year, 11, 31) };
  }
  const parsed = new Date(`${periodKey}T00:00:00`);
  return Number.isNaN(parsed.getTime()) ? null : { start: parsed, end: parsed };
};

const aggregateMetricValues = (values: number[], valueType: ValueType) => {
  if (values.length === 0) return undefined;
  if (valueType === "count" || valueType === "currency") {
    return values.reduce((sum, value) => sum + value, 0);
  }
  return values.reduce((sum, value) => sum + value, 0) / values.length;
};
const calculateChildValue = (values: Array<number | undefined>, valueType: ValueType) =>
  aggregateMetricValues(values.filter((value): value is number => typeof value === "number"), valueType);
const resolveMetricPersistence = (
  frequency: Frequency,
  _metricPersistence: MetricPersistence
): MetricPersistence => (frequency === "periodic" ? "carry_forward" : "period_only");

const shiftComparisonDate = (
  referenceDate: Date,
  frequency: Frequency,
  comparisonMode: ComparisonMode
) => {
  const nextDate = new Date(referenceDate);
  if (comparisonMode === "previous_period") {
    switch (frequency) {
      case "weekly":
        nextDate.setDate(nextDate.getDate() - 7);
        break;
      case "monthly":
        nextDate.setMonth(nextDate.getMonth() - 1);
        break;
      case "bimonthly":
        nextDate.setMonth(nextDate.getMonth() - 2);
        break;
      case "quarterly":
        nextDate.setMonth(nextDate.getMonth() - 3);
        break;
      case "yearly":
        nextDate.setFullYear(nextDate.getFullYear() - 1);
        break;
      case "periodic":
        nextDate.setDate(nextDate.getDate() - 1);
        break;
    }
    return nextDate;
  }
  if (comparisonMode === "same_period_last_year") {
    nextDate.setFullYear(nextDate.getFullYear() - 1);
  }
  return nextDate;
};

const shiftPeriodAnchorDate = (anchorDate: Date, frequency: Frequency, offset: number) => {
  const nextDate = new Date(anchorDate);
  switch (frequency) {
    case "weekly":
      nextDate.setDate(nextDate.getDate() + offset * 7);
      break;
    case "monthly":
      nextDate.setMonth(nextDate.getMonth() + offset);
      break;
    case "bimonthly":
      nextDate.setMonth(nextDate.getMonth() + offset * 2);
      break;
    case "quarterly":
      nextDate.setMonth(nextDate.getMonth() + offset * 3);
      break;
    case "yearly":
      nextDate.setFullYear(nextDate.getFullYear() + offset);
      break;
    case "periodic":
      nextDate.setDate(nextDate.getDate() + offset);
      break;
    default:
      break;
  }
  return nextDate;
};

const shiftPeriodKeyLastYear = (periodKey: string, frequency: Frequency) => {
  if (frequency === "weekly") {
    const match = /^(\d{4})-W(\d{2})$/.exec(periodKey);
    return match ? `${Number(match[1]) - 1}-W${match[2]}` : periodKey;
  }
  if (frequency === "monthly") {
    const match = /^(\d{4})-(\d{2})$/.exec(periodKey);
    return match ? `${Number(match[1]) - 1}-${match[2]}` : periodKey;
  }
  if (frequency === "bimonthly") {
    const match = /^(\d{4})-B([1-6])$/.exec(periodKey);
    return match ? `${Number(match[1]) - 1}-B${match[2]}` : periodKey;
  }
  if (frequency === "quarterly") {
    const match = /^(\d{4})-Q([1-4])$/.exec(periodKey);
    return match ? `${Number(match[1]) - 1}-Q${match[2]}` : periodKey;
  }
  if (frequency === "yearly") {
    return String(Number(periodKey) - 1);
  }
  const parsed = new Date(`${periodKey}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return periodKey;
  parsed.setFullYear(parsed.getFullYear() - 1);
  return parsed.toISOString().slice(0, 10);
};

const getPeriodAnchorDate = (date: Date, frequency: Frequency) => {
  const periodKey = getPeriodKey(date, frequency);
  const range = getPeriodRange(frequency, periodKey);
  return range?.start ?? getStartOfDay(date);
};

const getAggregationWindowPeriodKeys = (
  referenceDate: Date,
  frequency: Frequency,
  aggregationMode: AggregationMode
) => {
  if (aggregationMode === "current") {
    return [getPeriodKey(referenceDate, frequency)];
  }

  const aggregationStart = getAggregationStart(referenceDate, aggregationMode);
  if (!aggregationStart) return [getPeriodKey(referenceDate, frequency)];

  const keys: string[] = [];
  let cursor = getPeriodAnchorDate(referenceDate, frequency);

  while (cursor >= aggregationStart) {
    keys.unshift(getPeriodKey(cursor, frequency));
    cursor = shiftPeriodAnchorDate(cursor, frequency, -1);
  }

  return Array.from(new Set(keys.filter(Boolean)));
};

const getValueForContext = (
  history: MetricHistory,
  referenceDate: Date,
  frequency: Frequency,
  _scope: Scope,
  _campus: Campus,
  valueType: ValueType,
  aggregationMode: AggregationMode = "current",
  persistenceMode: MetricPersistence = "period_only"
): number | undefined => {
  const effectivePersistence = resolveMetricPersistence(frequency, persistenceMode);
  const periodKey = getPeriodKey(referenceDate, frequency);
  const filteredEntries = Object.entries(history).filter(([key, value]) => {
    const [entryPeriodKey, entryScopeKey] = key.split("::");
    return Boolean(entryPeriodKey && entryScopeKey && entryPeriodKey <= periodKey && typeof value === "number");
  });

  const getPeriodValue = (targetPeriodKey: string) => {
    const samePeriodEntries = Object.entries(history).filter(([key, value]) => {
      const [entryPeriodKey, entryScopeKey] = key.split("::");
      return entryPeriodKey === targetPeriodKey && Boolean(entryScopeKey) && typeof value === "number";
    });
    const globalEntry = samePeriodEntries.find(([key]) => key.endsWith("::global"));
    if (typeof globalEntry?.[1] === "number") return globalEntry[1];
    return aggregateMetricValues(
      samePeriodEntries.map(([, value]) => value).filter((value): value is number => typeof value === "number"),
      valueType
    );
  };

  const getLatestValue = () => {
    const latestPeriodKey = filteredEntries
      .map(([key]) => key.split("::")[0])
      .filter(Boolean)
      .sort((left, right) => left.localeCompare(right))
      .at(-1);
    return latestPeriodKey ? getPeriodValue(latestPeriodKey) : undefined;
  };

  if (aggregationMode !== "current") {
    const windowValues = getAggregationWindowPeriodKeys(referenceDate, frequency, aggregationMode)
      .map((entryPeriodKey) => getPeriodValue(entryPeriodKey))
      .filter((value): value is number => typeof value === "number");
    const aggregatedValue = aggregateMetricValues(windowValues, valueType);
    if (typeof aggregatedValue === "number") return aggregatedValue;
    return effectivePersistence === "carry_forward" ? getLatestValue() : undefined;
  }

  if (effectivePersistence === "carry_forward") return getLatestValue();
  return getPeriodValue(periodKey);
};

const getComparisonValueForContext = (
  history: MetricHistory,
  referenceDate: Date,
  frequency: Frequency,
  scope: Scope,
  campus: Campus,
  valueType: ValueType,
  aggregationMode: AggregationMode,
  comparisonMode: ComparisonMode
) => {
  if (comparisonMode === "previous_period" && aggregationMode !== "current") {
    const currentKeys = getAggregationWindowPeriodKeys(referenceDate, frequency, aggregationMode);
    if (currentKeys.length > 0) {
      const firstRange = getPeriodRange(frequency, currentKeys[0]);
      if (firstRange) {
        const previousValues = currentKeys
          .map((_, index) =>
            getValueForContext(
              history,
              shiftPeriodAnchorDate(firstRange.start, frequency, index - currentKeys.length),
              frequency,
              scope,
              campus,
              valueType,
              "current",
              "period_only"
            )
          )
          .filter((value): value is number => typeof value === "number");
        const aggregatedValue = aggregateMetricValues(previousValues, valueType);
        if (typeof aggregatedValue === "number") return aggregatedValue;
      }
    }
  }

  if (comparisonMode === "same_period_last_year") {
    const currentKeys = getAggregationWindowPeriodKeys(referenceDate, frequency, aggregationMode);
    const comparisonKeys = currentKeys.map((periodKey) =>
      shiftPeriodKeyLastYear(periodKey, frequency)
    );
    const comparisonValues = comparisonKeys
      .map((periodKey) =>
        getValueForContext(
          history,
          getPeriodRange(frequency, periodKey)?.start ?? comparisonDate,
          frequency,
          scope,
          campus,
          valueType,
          "current",
          "period_only"
        )
      )
      .filter((value): value is number => typeof value === "number");
    const aggregatedValue = aggregateMetricValues(comparisonValues, valueType);
    if (typeof aggregatedValue === "number") return aggregatedValue;
  }

  const comparisonDate = shiftComparisonDate(referenceDate, frequency, comparisonMode);
  return getValueForContext(
    history,
    comparisonDate,
    frequency,
    scope,
    campus,
    valueType,
    aggregationMode,
    "period_only"
  );
};
const getMetricKey = (
  referenceDate: Date,
  frequency: Frequency,
  _scope: Scope,
  _campus: Campus
) => {
  if (frequency === "weekly") {
    const iso = getIsoWeekParts(referenceDate);
    return `${iso.year}-W${String(iso.week).padStart(2, "0")}::global`;
  }
  if (frequency === "monthly") {
    return `${referenceDate.getFullYear()}-${padTwo(referenceDate.getMonth() + 1)}::global`;
  }
  if (frequency === "bimonthly") {
    return `${referenceDate.getFullYear()}-B${Math.floor(referenceDate.getMonth() / 2) + 1}::global`;
  }
  if (frequency === "quarterly") {
    return `${referenceDate.getFullYear()}-Q${Math.floor(referenceDate.getMonth() / 3) + 1}::global`;
  }
  if (frequency === "periodic") {
    return `${referenceDate.getFullYear()}-${padTwo(referenceDate.getMonth() + 1)}-${padTwo(referenceDate.getDate())}::global`;
  }
  return `${referenceDate.getFullYear()}::global`;
};
const setValueForContext = (
  history: MetricHistory,
  referenceDate: Date,
  frequency: Frequency,
  scope: Scope,
  campus: Campus,
  value: number | undefined
): MetricHistory => ({
  ...history,
  [getMetricKey(referenceDate, frequency, scope, campus)]: value,
});

const getDirectScore = (
  target: number | undefined,
  actual: number | undefined,
  performanceDirection: PerformanceDirection
) => {
  if (
    typeof target !== "number" ||
    typeof actual !== "number" ||
    Number.isNaN(target) ||
    Number.isNaN(actual) ||
    target < 0
  ) {
    return null;
  }
  if (target === 0) {
    if (actual === 0) return 1;
    return performanceDirection === "higher_is_better" ? Number.POSITIVE_INFINITY : 0;
  }
  const ratio = performanceDirection === "higher_is_better" ? actual / target : target / actual;
  return Number.isFinite(ratio) ? ratio : null;
};
const getStatusFromScore = (score: number | null): Status => {
  if (score === null) return "neutral";
  if (score >= 1.1) return "green_strong";
  if (score >= 0.95) return "green";
  if (score >= 0.75) return "yellow";
  return "red";
};
const getScoreFromRatingActual = (value: number | undefined): number | null => {
  if (typeof value !== "number" || Number.isNaN(value)) return null;
  if (value >= 9) return 1.1;
  if (value >= 7.5) return 0.95;
  if (value >= 6.5) return 0.75;
  return 0;
};
const statusColor = (status: Status) => {
  switch (status) {
    case "green_strong":
      return "#22c55e";
    case "green":
      return "#86efac";
    case "yellow":
      return "#facc15";
    case "red":
      return "#f87171";
    default:
      return "#d1d5db";
  }
};
const interpolateHexColor = (startHex: string, endHex: string, amount: number) => {
  const clampedAmount = Math.min(1, Math.max(0, amount));
  const start = Number.parseInt(startHex.slice(1), 16);
  const end = Number.parseInt(endHex.slice(1), 16);
  const startRgb = [(start >> 16) & 255, (start >> 8) & 255, start & 255];
  const endRgb = [(end >> 16) & 255, (end >> 8) & 255, end & 255];
  const nextRgb = startRgb.map((channel, index) =>
    Math.round(channel + (endRgb[index] - channel) * clampedAmount)
  );
  return `#${nextRgb.map((channel) => channel.toString(16).padStart(2, "0")).join("")}`;
};
const ratingColor = (value: number | undefined) => {
  if (typeof value !== "number" || Number.isNaN(value)) return null;
  const rating = Math.min(10, Math.max(1, value));
  const stops = [
    "#450a0a",
    "#7f1d1d",
    "#dc2626",
    "#ea580c",
    "#f97316",
    "#fb923c",
    "#facc15",
    "#86efac",
    "#22c55e",
    "#166534",
  ];
  const lowerRating = Math.floor(rating);
  const upperRating = Math.ceil(rating);
  const lowerColor = stops[lowerRating - 1];
  const upperColor = stops[upperRating - 1];
  return lowerRating === upperRating
    ? lowerColor
    : interpolateHexColor(lowerColor, upperColor, rating - lowerRating);
};
const kpiDisplayColor = (
  valueType: ValueType,
  actual: number | undefined,
  colorActual: number | undefined,
  status: Status
) =>
  typeof colorActual === "number"
    ? ratingColor(colorActual) ?? statusColor(status)
    : valueType === "rating_1_10"
      ? ratingColor(actual) ?? statusColor(status)
      : statusColor(status);
const hexToRgba = (hex: string, alpha: number) => {
  const normalized = hex.replace("#", "");
  const expanded =
    normalized.length === 3
      ? normalized
          .split("")
          .map((char) => `${char}${char}`)
          .join("")
      : normalized;
  const value = Number.parseInt(expanded, 16);
  if (Number.isNaN(value)) return `rgba(148,163,184,${alpha})`;
  const red = (value >> 16) & 255;
  const green = (value >> 8) & 255;
  const blue = value & 255;
  return `rgba(${red},${green},${blue},${alpha})`;
};
const isDateInFuture = (referenceDate: Date, eventDate: string | undefined) => {
  if (!eventDate) return false;
  const parsedEventDate = new Date(`${eventDate}T00:00:00`);
  if (Number.isNaN(parsedEventDate.getTime())) return false;
  return parsedEventDate.getTime() > getStartOfDay(referenceDate).getTime();
};
const formatAggregationLabel = (aggregationMode: AggregationMode) => {
  switch (aggregationMode) {
    case "mtd":
      return "MTD";
    case "qtd":
      return "QTD";
    case "ytd":
      return "YTD";
    default:
      return "Current Period";
  }
};
const formatComparisonLabel = (comparisonMode: ComparisonMode) => {
  switch (comparisonMode) {
    case "previous_period":
      return "Previous Period";
    case "same_period_last_year":
      return "Same Period Last Year";
    default:
      return "Target";
  }
};
const formatComparisonMetricLabel = (comparisonMode: ComparisonMode) => {
  switch (comparisonMode) {
    case "previous_period":
      return "Previous";
    case "same_period_last_year":
      return "Previous Year";
    default:
      return "Target";
  }
};
const formatReferenceDate = (date: Date, weekStartsOn: WeekStartsOn) =>
  `Wk ${getReportingWeekParts(date, weekStartsOn).week} | ${date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  })}`;
const shiftReferenceDate = (date: Date, direction: "previous" | "next") => {
  const step = direction === "previous" ? -7 : 7;
  const nextDate = new Date(date);
  nextDate.setDate(nextDate.getDate() + step);
  return nextDate;
};
const shiftDateByFrequency = (date: Date, frequency: Frequency, offset: number) => {
  const nextDate = new Date(date);
  switch (frequency) {
    case "weekly":
      nextDate.setDate(nextDate.getDate() + offset * 7);
      break;
    case "monthly":
      nextDate.setMonth(nextDate.getMonth() + offset);
      break;
    case "bimonthly":
      nextDate.setMonth(nextDate.getMonth() + offset * 2);
      break;
    case "quarterly":
      nextDate.setMonth(nextDate.getMonth() + offset * 3);
      break;
    case "yearly":
      nextDate.setFullYear(nextDate.getFullYear() + offset);
      break;
    case "periodic":
      nextDate.setDate(nextDate.getDate() + offset);
      break;
  }
  return nextDate;
};
const formatTrendPeriodLabel = (date: Date, frequency: Frequency) => {
  switch (frequency) {
    case "weekly":
      return `${date.getMonth() + 1}/${date.getDate()}`;
    case "monthly":
      return date.toLocaleDateString("en-US", { month: "short" });
    case "bimonthly":
      return `B${Math.floor(date.getMonth() / 2) + 1}`;
    case "quarterly":
      return `Q${Math.floor(date.getMonth() / 3) + 1}`;
    case "yearly":
      return `${date.getFullYear()}`;
    case "periodic":
      return `${date.getMonth() + 1}/${date.getDate()}`;
  }
};
const buildTrendPoints = (
  history: MetricHistory,
  referenceDate: Date,
  frequency: Frequency,
  valueType: ValueType
) =>
  Array.from({ length: 4 }, (_, index) => {
    const pointDate = shiftDateByFrequency(referenceDate, frequency, index - 3);
    return {
      label: formatTrendPeriodLabel(pointDate, frequency),
      value: getValueForContext(
        history,
        pointDate,
        frequency,
        "global",
        defaultCampus,
        valueType,
        "current",
        "period_only"
      ),
    };
  });

const buildCalculatedTrendPoints = (
  nodeId: string,
  rawNodes: PersistedNode[],
  rawEdges: PersistedEdge[],
  referenceDate: Date
) => {
  const rawNodeMap = new Map(rawNodes.map((node) => [node.id, node]));
  const incomingMap = new Map<string, PersistedEdge[]>();
  rawEdges.forEach((edge) => {
    if (!incomingMap.has(edge.target)) incomingMap.set(edge.target, []);
    incomingMap.get(edge.target)!.push(edge);
  });

  const getActualAtDate = (
    currentNodeId: string,
    pointDate: Date,
    visiting = new Set<string>()
  ): { value: number | undefined; frequency: Frequency; valueType: ValueType } => {
    const node = rawNodeMap.get(currentNodeId);
    if (!node || visiting.has(currentNodeId)) {
      return { value: undefined, frequency: "weekly", valueType: "count" };
    }

    const data = node.data ?? ({} as PersistedNodeData);
    const frequency = data.frequency ?? "weekly";
    const valueType = getNormalizedValueType(data.dataSource, data.valueType);
    const metricPersistence =
      data.metricPersistence === "carry_forward" || data.metricPersistence === "period_only"
        ? data.metricPersistence
        : data.frequency === "periodic" || data.dataSource === "Poll" || Boolean(data.eventDate)
          ? "carry_forward"
          : "period_only";
    const calculationMode = normalizeCalculationMode(data.calculationMode, data.isRollup);

    if (calculationMode === "formula_percentage") {
      const nextVisiting = new Set(visiting);
      nextVisiting.add(currentNodeId);
      const numeratorResult = data.formulaNumeratorKpiId
        ? getActualAtDate(data.formulaNumeratorKpiId, pointDate, nextVisiting)
        : { value: undefined, frequency, valueType: "percentage" as ValueType };
      const denominatorResult = data.formulaDenominatorKpiId
        ? getActualAtDate(data.formulaDenominatorKpiId, pointDate, nextVisiting)
        : { value: undefined, frequency, valueType: "percentage" as ValueType };
      return {
        value: calculateFormulaPercentage(
          numeratorResult.value,
          denominatorResult.value
        ),
        frequency,
        valueType: "percentage",
      };
    }

    if (calculationMode !== "calculated_values") {
      return {
        value: getValueForContext(
          data.actualHistory ?? {},
          pointDate,
          frequency,
          "global",
          defaultCampus,
          valueType,
          "current",
          metricPersistence
        ),
        frequency,
        valueType,
      };
    }

    const incomingEdges = incomingMap.get(currentNodeId) ?? [];
    if (incomingEdges.length === 0) {
      return { value: undefined, frequency, valueType };
    }

    const nextVisiting = new Set(visiting);
    nextVisiting.add(currentNodeId);

    const childValues = incomingEdges.map((edge) => getActualAtDate(edge.source, pointDate, nextVisiting));
    const uniqueChildValueTypes = Array.from(new Set(childValues.map((child) => child.valueType)));
    if (uniqueChildValueTypes.length > 1) {
      return { value: undefined, frequency, valueType };
    }

    return {
      value: calculateChildValue(
        childValues.map((child) => child.value),
        uniqueChildValueTypes[0] ?? valueType
      ),
      frequency,
      valueType: uniqueChildValueTypes[0] ?? valueType,
    };
  };

  const rootNode = rawNodeMap.get(nodeId);
  const rootData = rootNode?.data ?? ({} as PersistedNodeData);
  const rootFrequency = rootData.frequency ?? "weekly";

  return Array.from({ length: 4 }, (_, index) => {
    const pointDate = shiftDateByFrequency(referenceDate, rootFrequency, index - 3);
    const result = getActualAtDate(nodeId, pointDate);
    return {
      label: formatTrendPeriodLabel(pointDate, rootFrequency),
      value: result.value,
    };
  });
};
const formatMetricValue = (value: number | undefined, valueType: ValueType) => {
  if (typeof value !== "number") return "—";
  if (valueType === "currency") {
    return value.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
  }
  if (valueType === "percentage") {
    return `${value.toFixed(1)}%`;
  }
  if (valueType === "rating_1_10") {
    return value.toFixed(1);
  }
  return value.toLocaleString("en-US");
};

function Sparkline({
  points,
  color,
  valueType,
  width = 180,
  height = 46,
}: {
  points: Array<{ label: string; value: number | undefined }>;
  color: string;
  valueType: ValueType;
  width?: number;
  height?: number;
}) {
  const definedPoints = points.filter(
    (point): point is { label: string; value: number } => typeof point.value === "number"
  );

  if (definedPoints.length === 0) {
    return <div style={{ fontSize: 12, color: "#94a3b8" }}>No trend data yet.</div>;
  }

  const values = definedPoints.map((point) => point.value);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const topPadding = Math.max(6, Math.round(height * 0.17));
  const bottomPadding = Math.max(4, Math.round(height * 0.05));
  const chartFloor = height - bottomPadding;
  const plotHeight = Math.max(20, chartFloor - topPadding);
  const polylinePoints = points
    .map((point, index) => {
      if (typeof point.value !== "number") return null;
      const x = (index / Math.max(points.length - 1, 1)) * width;
      const y = chartFloor - ((point.value - min) / range) * plotHeight;
      return `${x},${y}`;
    })
    .filter((value): value is string => Boolean(value))
    .join(" ");

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "auto 1fr",
        gap: 10,
        alignItems: "stretch",
      }}
        >
          <div
            style={{
              display: "grid",
          gridTemplateRows: "1fr 1fr",
          alignItems: "space-between",
          minWidth: 72,
        }}
      >
        <div style={{ fontSize: 11, fontWeight: 700, color: "#cbd5e1", alignSelf: "start" }}>
          {formatMetricValue(max, valueType)}
        </div>
        <div style={{ fontSize: 11, fontWeight: 700, color: "#cbd5e1", alignSelf: "end" }}>
          {formatMetricValue(min, valueType)}
        </div>
      </div>
      <div style={{ display: "grid", gap: 6 }}>
        <svg
          width={width}
          height={height}
          viewBox={`0 0 ${width} ${height}`}
          style={{ display: "block", overflow: "visible" }}
        >
          <line x1="0" y1={topPadding} x2={width} y2={topPadding} stroke="rgba(148,163,184,0.34)" strokeWidth="1" strokeDasharray="3 4" />
          <line x1="0" y1={chartFloor} x2={width} y2={chartFloor} stroke="rgba(148,163,184,0.34)" strokeWidth="1" strokeDasharray="3 4" />
          <polyline
            fill="none"
            stroke={color}
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
            points={polylinePoints}
          />
        </svg>
        <div
          style={{
            width,
            display: "grid",
            gridTemplateColumns: `repeat(${points.length}, minmax(0, 1fr))`,
            gap: 4,
          }}
        >
          {points.map((point) => (
            <div
              key={point.label}
              style={{
                fontSize: 10,
                fontWeight: 700,
                color: "#94a3b8",
                textAlign: "center",
                whiteSpace: "nowrap",
              }}
            >
              {point.label}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function Dashboard({
  signedInUser = null,
  onSignOut,
}: {
  signedInUser?: SignedInUser | null;
  onSignOut?: () => void;
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [enterprises, setEnterprises] = React.useState<EnterpriseSummary[]>([]);
  const [selectedEnterpriseId, setSelectedEnterpriseId] = React.useState<number | null>(null);
  const [workspace, setWorkspace] = React.useState<Workspace | null>(null);
  const skipNextWorkspaceAutosaveRef = React.useRef(false);
  const [isLoading, setIsLoading] = React.useState(true);
  const [isSaving, setIsSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [editingKpiId, setEditingKpiId] = React.useState<string | null>(null);
  const [selectedOwner, setSelectedOwner] = React.useState<string>("");
  const [referenceDate, setReferenceDate] = React.useState<Date>(initialReferenceDate);
  const [aggregationMode, setAggregationMode] = React.useState<AggregationMode>("current");
  const [comparisonMode, setComparisonMode] = React.useState<ComparisonMode>("target");
  const [searchTerm, setSearchTerm] = React.useState("");
  const [isCustomizeOpen, setIsCustomizeOpen] = React.useState(false);
  const [isSettingsLensesCollapsed, setIsSettingsLensesCollapsed] = React.useState(true);
  const [isSettingsKpisCollapsed, setIsSettingsKpisCollapsed] = React.useState(true);
  const [isSettingsReferenceListsCollapsed, setIsSettingsReferenceListsCollapsed] = React.useState(true);
  const [isKpiWorkOpen, setIsKpiWorkOpen] = React.useState(false);
  const [draggedDashboardKpiId, setDraggedDashboardKpiId] = React.useState<string | null>(null);
  const [expandedParentIds, setExpandedParentIds] = React.useState<string[]>([]);
  const [dashboardZoom, setDashboardZoom] = React.useState(1);
  const [dashboardPan, setDashboardPan] = React.useState({ x: 0, y: 0 });
  const dashboardCanvasRef = React.useRef<HTMLDivElement | null>(null);
  const dashboardContentRef = React.useRef<HTMLDivElement | null>(null);
  const dashboardZoomRef = React.useRef(dashboardZoom);
  const dashboardPanRef = React.useRef(dashboardPan);
  const dashboardPanDragRef = React.useRef<{
    pointerId: number;
    startX: number;
    startY: number;
    originX: number;
    originY: number;
  } | null>(null);
  const dashboardTouchPointersRef = React.useRef(new Map<number, { x: number; y: number }>());
  const dashboardPinchRef = React.useRef<{
    startDistance: number;
    startZoom: number;
    startPan: { x: number; y: number };
  } | null>(null);
  const dashboardNativePinchRef = React.useRef<{
    startDistance: number;
    startZoom: number;
    startPan: { x: number; y: number };
  } | null>(null);

  const dashboardCardMinWidth = 320;
  const dashboardCardPadding = 18;
  const dashboardCardGap = 18;
  const dashboardMetricFontSize = 24;
  const dashboardTitleFontSize = 18;
  const dashboardBodyFontSize = 13;
  const dashboardLabelFontSize = 11;
  const dashboardChartWidth = 180;
  const dashboardChartHeight = 46;

  React.useEffect(() => {
    dashboardZoomRef.current = dashboardZoom;
  }, [dashboardZoom]);

  React.useEffect(() => {
    dashboardPanRef.current = dashboardPan;
  }, [dashboardPan]);

  const dashboardMinZoom = 0.05;
  const dashboardMaxZoom = 2.5;
  const clampDashboardZoom = React.useCallback(
    (value: number) => Math.min(dashboardMaxZoom, Math.max(dashboardMinZoom, value)),
    []
  );
  const setDashboardViewport = React.useCallback((nextZoom: number, nextPan: { x: number; y: number }) => {
    dashboardZoomRef.current = nextZoom;
    dashboardPanRef.current = nextPan;
    setDashboardZoom(nextZoom);
    setDashboardPan(nextPan);
  }, []);

  const zoomDashboardAroundPoint = React.useCallback(
    (nextZoomValue: number, viewportX: number, viewportY: number) => {
      const currentZoom = dashboardZoomRef.current;
      const nextZoom = clampDashboardZoom(nextZoomValue);
      if (nextZoom === currentZoom) return;

      const zoomRatio = nextZoom / currentZoom;
      const currentPan = dashboardPanRef.current;
      const nextPan = {
        x: viewportX - (viewportX - currentPan.x) * zoomRatio,
        y: viewportY - (viewportY - currentPan.y) * zoomRatio,
      };

      setDashboardViewport(nextZoom, nextPan);
    },
    [clampDashboardZoom, setDashboardViewport]
  );

  const onDashboardCanvasWheel = React.useCallback(
    (event: React.WheelEvent<HTMLDivElement>) => {
      event.preventDefault();
      const rect = event.currentTarget.getBoundingClientRect();
      const cursorX = event.clientX - rect.left;
      const cursorY = event.clientY - rect.top;
      const normalizedDelta =
        event.deltaMode === 1
          ? event.deltaY * 16
          : event.deltaMode === 2
            ? event.deltaY * 240
            : event.deltaY;
      const zoomFactor = Math.exp(-normalizedDelta * 0.0015);
      zoomDashboardAroundPoint(
        dashboardZoomRef.current * zoomFactor,
        cursorX,
        cursorY
      );
    },
    [zoomDashboardAroundPoint]
  );

  const onDashboardCanvasPointerDown = React.useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (dashboardNativePinchRef.current) return;

      if (event.pointerType === "touch") {
        event.preventDefault();
        dashboardTouchPointersRef.current.set(event.pointerId, {
          x: event.clientX,
          y: event.clientY,
        });
        event.currentTarget.setPointerCapture(event.pointerId);

        const touches = Array.from(dashboardTouchPointersRef.current.values());
        if (touches.length === 1) {
          dashboardPanDragRef.current = {
            pointerId: event.pointerId,
            startX: event.clientX,
            startY: event.clientY,
            originX: dashboardPanRef.current.x,
            originY: dashboardPanRef.current.y,
          };
        }
        if (touches.length === 2) {
          const [first, second] = touches;
          dashboardPanDragRef.current = null;
          dashboardPinchRef.current = {
            startDistance: Math.hypot(second.x - first.x, second.y - first.y),
            startZoom: dashboardZoomRef.current,
            startPan: dashboardPanRef.current,
          };
        }
        return;
      }

      if (event.button !== 0) return;
      const target = event.target as HTMLElement;
      if (target.closest("[data-dashboard-card], button, input, select, textarea, a, label")) return;

      dashboardPanDragRef.current = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        originX: dashboardPanRef.current.x,
        originY: dashboardPanRef.current.y,
      };
      event.currentTarget.setPointerCapture(event.pointerId);
    },
    []
  );

  const onDashboardCanvasPointerMove = React.useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    if (dashboardNativePinchRef.current) return;

    if (event.pointerType === "touch" && dashboardTouchPointersRef.current.has(event.pointerId)) {
      dashboardTouchPointersRef.current.set(event.pointerId, {
        x: event.clientX,
        y: event.clientY,
      });

      const pinch = dashboardPinchRef.current;
      const touches = Array.from(dashboardTouchPointersRef.current.values());
      if (pinch && touches.length >= 2) {
        event.preventDefault();
        const [first, second] = touches;
        const distance = Math.hypot(second.x - first.x, second.y - first.y);
        const rect = event.currentTarget.getBoundingClientRect();
        const centerX = (first.x + second.x) / 2 - rect.left;
        const centerY = (first.y + second.y) / 2 - rect.top;
        const nextZoom = clampDashboardZoom(pinch.startZoom * (distance / pinch.startDistance));
        const zoomRatio = nextZoom / pinch.startZoom;
        const nextPan = {
          x: centerX - (centerX - pinch.startPan.x) * zoomRatio,
          y: centerY - (centerY - pinch.startPan.y) * zoomRatio,
        };
        setDashboardViewport(nextZoom, nextPan);
        return;
      }
    }

    const drag = dashboardPanDragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    if (event.pointerType === "touch") {
      event.preventDefault();
    }
    const nextPan = {
      x: drag.originX + event.clientX - drag.startX,
      y: drag.originY + event.clientY - drag.startY,
    };
    setDashboardViewport(dashboardZoomRef.current, nextPan);
  }, [clampDashboardZoom, setDashboardViewport]);

  const onDashboardCanvasPointerEnd = React.useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    if (event.pointerType === "touch") {
      dashboardTouchPointersRef.current.delete(event.pointerId);
      if (dashboardTouchPointersRef.current.size < 2) {
        dashboardPinchRef.current = null;
      }
      const remainingTouch = Array.from(dashboardTouchPointersRef.current.entries())[0];
      if (remainingTouch) {
        const [pointerId, touch] = remainingTouch;
        dashboardPanDragRef.current = {
          pointerId,
          startX: touch.x,
          startY: touch.y,
          originX: dashboardPanRef.current.x,
          originY: dashboardPanRef.current.y,
        };
      }
    }
    if (dashboardPanDragRef.current?.pointerId === event.pointerId) {
      dashboardPanDragRef.current = null;
    }
  }, []);

  const onDashboardZoomControl = React.useCallback((direction: "in" | "out") => {
    const canvas = dashboardCanvasRef.current;
    const centerX = canvas ? canvas.clientWidth / 2 : 0;
    const centerY = canvas ? canvas.clientHeight / 2 : 0;
    const zoomFactor = direction === "in" ? 1.2 : 1 / 1.2;
    zoomDashboardAroundPoint(dashboardZoomRef.current * zoomFactor, centerX, centerY);
  }, [zoomDashboardAroundPoint]);

  const onDashboardFitView = React.useCallback(() => {
    const canvas = dashboardCanvasRef.current;
    const content = dashboardContentRef.current;
    if (!canvas || !content) {
      setDashboardViewport(1, { x: 0, y: 0 });
      return;
    }

    const contentWidth = content.offsetWidth;
    const contentHeight = content.offsetHeight;
    if (contentWidth <= 0 || contentHeight <= 0) {
      setDashboardViewport(1, { x: 0, y: 0 });
      return;
    }

    const padding = 32;
    const nextZoom = clampDashboardZoom(
      Math.min(
        (canvas.clientWidth - padding * 2) / contentWidth,
        (canvas.clientHeight - padding * 2) / contentHeight,
        1
      )
    );
    const nextPan = {
      x: (canvas.clientWidth - contentWidth * nextZoom) / 2,
      y: (canvas.clientHeight - contentHeight * nextZoom) / 2,
    };
    setDashboardViewport(nextZoom, nextPan);
  }, [clampDashboardZoom, setDashboardViewport]);

  React.useEffect(() => {
    const canvas = dashboardCanvasRef.current;
    if (!canvas) return;

    const getTouchPinchGeometry = (touches: TouchList) => {
      if (touches.length < 2) return null;
      const [first, second] = [touches[0], touches[1]];
      const rect = canvas.getBoundingClientRect();
      return {
        distance: Math.hypot(second.clientX - first.clientX, second.clientY - first.clientY),
        centerX: (first.clientX + second.clientX) / 2 - rect.left,
        centerY: (first.clientY + second.clientY) / 2 - rect.top,
      };
    };

    const onTouchStart = (event: TouchEvent) => {
      if (event.touches.length < 2) return;
      const geometry = getTouchPinchGeometry(event.touches);
      if (!geometry || geometry.distance <= 0) return;
      event.preventDefault();
      event.stopPropagation();
      dashboardPanDragRef.current = null;
      dashboardPinchRef.current = null;
      dashboardTouchPointersRef.current.clear();
      dashboardNativePinchRef.current = {
        startDistance: geometry.distance,
        startZoom: dashboardZoomRef.current,
        startPan: dashboardPanRef.current,
      };
    };

    const onTouchMove = (event: TouchEvent) => {
      const pinch = dashboardNativePinchRef.current;
      if (!pinch || event.touches.length < 2) return;
      const geometry = getTouchPinchGeometry(event.touches);
      if (!geometry || pinch.startDistance <= 0) return;
      event.preventDefault();
      event.stopPropagation();
      const nextZoom = clampDashboardZoom(pinch.startZoom * (geometry.distance / pinch.startDistance));
      const zoomRatio = nextZoom / pinch.startZoom;
      setDashboardViewport(nextZoom, {
        x: geometry.centerX - (geometry.centerX - pinch.startPan.x) * zoomRatio,
        y: geometry.centerY - (geometry.centerY - pinch.startPan.y) * zoomRatio,
      });
    };

    const onTouchEnd = (event: TouchEvent) => {
      if (event.touches.length >= 2) {
        const geometry = getTouchPinchGeometry(event.touches);
        if (!geometry || geometry.distance <= 0) return;
        dashboardNativePinchRef.current = {
          startDistance: geometry.distance,
          startZoom: dashboardZoomRef.current,
          startPan: dashboardPanRef.current,
        };
        return;
      }
      dashboardNativePinchRef.current = null;
    };

    let gestureStartZoom = dashboardZoomRef.current;
    const onGestureStart = (event: Event) => {
      event.preventDefault();
      gestureStartZoom = dashboardZoomRef.current;
    };
    const onGestureChange = (event: Event) => {
      event.preventDefault();
      const gestureEvent = event as Event & { scale?: number; clientX?: number; clientY?: number };
      const rect = canvas.getBoundingClientRect();
      const centerX =
        typeof gestureEvent.clientX === "number" ? gestureEvent.clientX - rect.left : rect.width / 2;
      const centerY =
        typeof gestureEvent.clientY === "number" ? gestureEvent.clientY - rect.top : rect.height / 2;
      zoomDashboardAroundPoint(gestureStartZoom * (gestureEvent.scale ?? 1), centerX, centerY);
    };

    canvas.addEventListener("touchstart", onTouchStart, { capture: true, passive: false });
    window.addEventListener("touchmove", onTouchMove, { capture: true, passive: false });
    window.addEventListener("touchend", onTouchEnd, { capture: true, passive: false });
    window.addEventListener("touchcancel", onTouchEnd, { capture: true, passive: false });
    canvas.addEventListener("gesturestart", onGestureStart, { passive: false });
    canvas.addEventListener("gesturechange", onGestureChange, { passive: false });
    return () => {
      canvas.removeEventListener("touchstart", onTouchStart, { capture: true });
      window.removeEventListener("touchmove", onTouchMove, { capture: true });
      window.removeEventListener("touchend", onTouchEnd, { capture: true });
      window.removeEventListener("touchcancel", onTouchEnd, { capture: true });
      canvas.removeEventListener("gesturestart", onGestureStart);
      canvas.removeEventListener("gesturechange", onGestureChange);
    };
  }, [clampDashboardZoom, setDashboardViewport, zoomDashboardAroundPoint]);

  React.useEffect(() => {
    const loadEnterprises = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await api.get("/enterprises/");
        const nextEnterprises = (response.data as EnterpriseSummary[]).slice().sort((left, right) => {
          const leftUpdatedAt = left.updated_at ? new Date(left.updated_at).getTime() : 0;
          const rightUpdatedAt = right.updated_at ? new Date(right.updated_at).getTime() : 0;
          return rightUpdatedAt - leftUpdatedAt;
        });
        setEnterprises(nextEnterprises);
        const queryEnterpriseId = Number(searchParams.get("enterpriseId"));
        const storedEnterpriseId = Number(window.localStorage.getItem(activeEnterpriseStorageKey));
        const preferredEnterprise =
          nextEnterprises.find((enterprise) => enterprise.id === queryEnterpriseId) ??
          nextEnterprises.find((enterprise) => enterprise.id === storedEnterpriseId) ??
          nextEnterprises[0] ??
          null;
        setSelectedEnterpriseId(preferredEnterprise?.id ?? null);
      } catch (loadError) {
        console.error("Failed to load enterprises for dashboard", loadError);
        setError("Could not load enterprises from the backend.");
      } finally {
        setIsLoading(false);
      }
    };
    void loadEnterprises();
  }, [searchParams]);

  React.useEffect(() => {
    if (selectedEnterpriseId === null) return;
    const loadWorkspace = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const cacheEnterprise = enterprises.find((enterprise) => enterprise.id === selectedEnterpriseId) ?? null;
        const cachedWorkspace = window.localStorage.getItem(getWorkspaceCacheKey(selectedEnterpriseId, cacheEnterprise));
        if (cachedWorkspace) {
          try {
            setWorkspace(normalizeWorkspace(JSON.parse(cachedWorkspace) as Workspace));
          } catch {}
        }
        const response = await api.get(`/enterprises/${selectedEnterpriseId}/workspace`);
        const nextWorkspace = normalizeWorkspace((response.data.workspace ?? {}) as Workspace);
        skipNextWorkspaceAutosaveRef.current = true;
        setWorkspace(nextWorkspace);
        window.localStorage.setItem(activeEnterpriseStorageKey, String(selectedEnterpriseId));
        window.localStorage.setItem(getWorkspaceCacheKey(selectedEnterpriseId, cacheEnterprise), JSON.stringify(nextWorkspace));
      } catch (loadError) {
        console.error("Failed to load workspace for dashboard", loadError);
        setError("Could not load the saved workspace for this enterprise.");
      } finally {
        setIsLoading(false);
      }
    };
    void loadWorkspace();
  }, [enterprises, selectedEnterpriseId]);

  const refreshWorkspaceFromServer = React.useCallback(async () => {
    if (selectedEnterpriseId === null) return;

    try {
      const response = await api.get(`/enterprises/${selectedEnterpriseId}/workspace`);
      const nextWorkspace = normalizeWorkspace((response.data.workspace ?? {}) as Workspace);
      const cacheEnterprise = enterprises.find((enterprise) => enterprise.id === selectedEnterpriseId) ?? null;
      skipNextWorkspaceAutosaveRef.current = true;
      setWorkspace(nextWorkspace);
      window.localStorage.setItem(activeEnterpriseStorageKey, String(selectedEnterpriseId));
      window.localStorage.setItem(getWorkspaceCacheKey(selectedEnterpriseId, cacheEnterprise), JSON.stringify(nextWorkspace));
    } catch (loadError) {
      console.error("Failed to refresh dashboard workspace", loadError);
    }
  }, [enterprises, selectedEnterpriseId]);

  React.useEffect(
    () => subscribeToWorkspaceChanged(selectedEnterpriseId, () => void refreshWorkspaceFromServer()),
    [refreshWorkspaceFromServer, selectedEnterpriseId]
  );

  React.useEffect(() => {
    if (selectedEnterpriseId === null) return;

    const refreshWhenVisible = () => {
      if (document.visibilityState !== "visible") return;
      void refreshWorkspaceFromServer();
    };

    window.addEventListener("focus", refreshWhenVisible);
    document.addEventListener("visibilitychange", refreshWhenVisible);

    return () => {
      window.removeEventListener("focus", refreshWhenVisible);
      document.removeEventListener("visibilitychange", refreshWhenVisible);
    };
  }, [refreshWorkspaceFromServer, selectedEnterpriseId]);

  const selectedEnterprise =
    selectedEnterpriseId === null
      ? null
      : enterprises.find((enterprise) => enterprise.id === selectedEnterpriseId) ?? null;
  const weekStartsOn = normalizeWeekStartsOn(selectedEnterprise?.week_starts_on);

  React.useEffect(() => {
    const defaultReferenceDate = getInitialReferenceDate(weekStartsOn);
    setReferenceDate(defaultReferenceDate);
    setAggregationMode(
      workspace?.aggregationMode === "mtd" ||
        workspace?.aggregationMode === "qtd" ||
        workspace?.aggregationMode === "ytd" ||
        workspace?.aggregationMode === "current"
        ? workspace.aggregationMode
        : "current"
    );
    setComparisonMode(
      workspace?.comparisonMode === "previous_period" ||
        workspace?.comparisonMode === "same_period_last_year" ||
        workspace?.comparisonMode === "target"
        ? workspace.comparisonMode
        : "target"
    );
  }, [weekStartsOn, workspace?.aggregationMode, workspace?.comparisonMode, workspace?.referenceDate]);

  const ownerOptions = React.useMemo(() => {
    const listedOwners = (workspace?.ownerList ?? []).filter((owner) => owner.trim().length > 0);
    const nodeOwners = (workspace?.allNodes ?? [])
      .map((node) => node.data.owner?.trim() ?? "")
      .filter((owner) => owner.length > 0);
    return Array.from(new Set([...listedOwners, ...nodeOwners])).sort((left, right) =>
      left.localeCompare(right)
    );
  }, [workspace?.allNodes, workspace?.ownerList]);

  const workspaceLensConfigs = React.useMemo(
    () => workspace?.lensConfigs ?? {},
    [workspace?.lensConfigs]
  );

  const workspaceLenses = React.useMemo(() => {
    const configuredLenses = workspace?.availableLenses ?? [];
    const fallbackLenses = Object.keys(workspaceLensConfigs);
    return Array.from(new Set([...configuredLenses, ...fallbackLenses])).filter(Boolean);
  }, [workspace?.availableLenses, workspaceLensConfigs]);

  const selectedPersistedKpi = React.useMemo(() => {
    if (editingKpiId === null) return null;
    return workspace?.allNodes?.find((node) => node.id === editingKpiId) ?? null;
  }, [editingKpiId, workspace?.allNodes]);

  React.useEffect(() => {
    if (!ownerOptions.length) {
      setSelectedOwner("");
      return;
    }
    if (selectedOwner && ownerOptions.includes(selectedOwner)) return;
    const queryOwner = searchParams.get("owner");
    if (queryOwner && ownerOptions.includes(queryOwner)) {
      setSelectedOwner(queryOwner);
      return;
    }
    const storedOwner = window.localStorage.getItem(activeDashboardOwnerStorageKey);
    if (storedOwner && ownerOptions.includes(storedOwner)) {
      setSelectedOwner(storedOwner);
      return;
    }
    setSelectedOwner(ownerOptions[0]);
  }, [ownerOptions, searchParams, selectedOwner]);

  React.useEffect(() => {
    if (!selectedOwner) return;
    window.localStorage.setItem(activeDashboardOwnerStorageKey, selectedOwner);
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      next.set("owner", selectedOwner);
      if (selectedEnterpriseId !== null) {
        next.set("enterpriseId", String(selectedEnterpriseId));
      }
      return next;
    }, { replace: true });
  }, [selectedEnterpriseId, selectedOwner, setSearchParams]);

  const dashboardNodes = React.useMemo<DashboardNode[]>(() => {
    const rawNodes = workspace?.allNodes ?? [];
    const rawEdges = workspace?.allEdges ?? [];

    const baseNodes = rawNodes.map((node) => {
      const data = node.data ?? ({} as PersistedNodeData);
      const metricPersistence =
        data.metricPersistence === "carry_forward" || data.metricPersistence === "period_only"
          ? data.metricPersistence
          : data.frequency === "periodic" || data.dataSource === "Poll" || Boolean(data.eventDate)
            ? "carry_forward"
            : "period_only";
      const frequency = data.frequency ?? "weekly";
      const valueType = getNormalizedValueType(data.dataSource, data.valueType);
      const calculationMode = normalizeCalculationMode(data.calculationMode, data.isRollup);
      const target =
        comparisonMode === "target"
          ? getValueForContext(
              data.targetHistory ?? {},
              referenceDate,
              frequency,
              "global",
              defaultCampus,
              valueType,
              aggregationMode,
              metricPersistence
            )
          : getComparisonValueForContext(
              data.actualHistory ?? {},
              referenceDate,
              frequency,
              "global",
              defaultCampus,
              valueType,
              aggregationMode,
              comparisonMode
            );
      const actual = getValueForContext(
        data.actualHistory ?? {},
        referenceDate,
        frequency,
        "global",
        defaultCampus,
        valueType,
        aggregationMode,
        metricPersistence
      );

      return {
        id: node.id,
        label: data.label ?? "New KPI",
        owner: data.owner ?? "Unassigned",
        target,
        actual,
        frequency,
        valueType,
        unit: data.unit ?? "",
        performanceDirection: data.performanceDirection ?? "higher_is_better",
        calculationMode,
        status: data.status ?? "neutral" as Status,
        statusNote: isDateInFuture(referenceDate, data.eventDate)
          ? "Upcoming"
          : aggregationMode !== "current"
            ? `${formatAggregationLabel(aggregationMode)} view`
            : undefined,
        colorActual: data.colorActual,
        metricsRestricted: data.metricsRestricted ?? false,
        actualHistory: data.actualHistory ?? {},
      };
    });

    const nodeMap = new Map(baseNodes.map((node) => [node.id, node]));
    const incomingMap = new Map<string, PersistedEdge[]>();
    rawEdges.forEach((edge) => {
      if (!incomingMap.has(edge.target)) incomingMap.set(edge.target, []);
      incomingMap.get(edge.target)!.push(edge);
    });

    const computeScore = (
      nodeId: string,
      visiting = new Set<string>()
    ): { score: number | null; statusNote?: string; target?: number; actual?: number; colorActual?: number } => {
      if (visiting.has(nodeId)) return { score: null, statusNote: "Circular dependency detected" };
      const node = nodeMap.get(nodeId);
      if (!node) return { score: null, statusNote: "Missing KPI" };
      if (node.metricsRestricted) {
        return {
          score: null,
          target: node.target,
          actual: node.actual,
          colorActual: node.colorActual,
        };
      }
      if (node.calculationMode === "direct") {
        if (node.statusNote === "Upcoming") return { score: null, statusNote: node.statusNote };
        return {
          score: getDirectScore(node.target, node.actual, node.performanceDirection),
          target: node.target,
          actual: node.actual,
          colorActual: node.valueType === "rating_1_10" ? node.actual : undefined,
          statusNote: node.statusNote,
        };
      }

      if (node.calculationMode === "formula_percentage") {
        const persistedNode = rawNodes.find((candidate) => candidate.id === nodeId);
        const numeratorId = persistedNode?.data.formulaNumeratorKpiId;
        const denominatorId = persistedNode?.data.formulaDenominatorKpiId;

        if (!numeratorId || !denominatorId) {
          return {
            score: null,
            statusNote: "Select numerator and denominator KPIs for this percentage formula",
          };
        }

        const nextVisiting = new Set(visiting);
        nextVisiting.add(nodeId);
        const numeratorResult = computeScore(numeratorId, nextVisiting);
        const denominatorResult = computeScore(denominatorId, nextVisiting);

        if (numeratorResult.statusNote || denominatorResult.statusNote) {
          return {
            score: null,
            statusNote: "Numerator or denominator KPI needs attention before this percentage can calculate",
          };
        }

        const actual = calculateFormulaPercentage(
          numeratorResult.actual,
          denominatorResult.actual
        );
        if (
          typeof denominatorResult.actual === "number" &&
          denominatorResult.actual <= 0
        ) {
          return { score: null, statusNote: "Actual denominator must be greater than 0" };
        }
        if (typeof node.target !== "number") {
          return { score: null, actual, statusNote: "Set a target for this percentage KPI" };
        }
        if (typeof actual !== "number") {
          return {
            score: null,
            target: node.target,
            actual,
            statusNote: "Waiting on numerator and denominator actual values",
          };
        }

        return {
          score: getDirectScore(node.target, actual, node.performanceDirection),
          target: node.target,
          actual,
          colorActual: node.valueType === "rating_1_10" ? actual : undefined,
        };
      }

      const incomingEdges = incomingMap.get(nodeId) ?? [];
      if (incomingEdges.length === 0) {
        return { score: null, statusNote: "Calculated KPI has no child KPIs" };
      }

      if (node.calculationMode === "calculated_values") {
        const childNodes = incomingEdges
          .map((edge) => nodeMap.get(edge.source))
          .filter((childNode): childNode is NonNullable<typeof childNode> => Boolean(childNode));
        const uniqueChildValueTypes = Array.from(new Set(childNodes.map((childNode) => childNode.valueType)));
        if (uniqueChildValueTypes.length > 1) {
          return {
            score: null,
            statusNote: `Mixed child value types (${uniqueChildValueTypes
              .map((valueType) => formatValueTypeLabel(valueType))
              .join(", ")})`,
          };
        }
        const rollupValueType = uniqueChildValueTypes[0] ?? node.valueType;
        const childResults = incomingEdges.map((edge) => {
          const nextVisiting = new Set(visiting);
          nextVisiting.add(nodeId);
          return computeScore(edge.source, nextVisiting);
        });
        const target = calculateChildValue(childResults.map((result) => result.target), rollupValueType);
        const actual = calculateChildValue(childResults.map((result) => result.actual), rollupValueType);
        const hasMissingTarget = childResults.some((result) => typeof result.target !== "number");
        const hasMissingActual = childResults.some((result) => typeof result.actual !== "number");
        if (
          hasMissingTarget ||
          hasMissingActual ||
          typeof target !== "number" ||
          typeof actual !== "number"
        ) {
          return {
            score: null,
            target,
            actual,
            statusNote:
              hasMissingTarget && hasMissingActual
                ? "Waiting on child KPI targets and actuals"
                : hasMissingTarget
                  ? "Waiting on child KPI targets"
                  : "Waiting on child KPI actuals",
          };
        }
        return {
          score: getDirectScore(target, actual, node.performanceDirection),
          target,
          actual,
          colorActual: node.valueType === "rating_1_10" ? actual : undefined,
        };
      }

      let configuredWeightTotal = 0;
      let weightedSum = 0;
      let validWeightTotal = 0;
      let weightedColorActualSum = 0;
      let validColorActualWeightTotal = 0;
      let missingWeight = false;
      let missingChildScore = false;

      for (const edge of incomingEdges) {
        const influence = typeof edge.data?.influence === "number" ? edge.data.influence / 100 : null;
        if (influence === null) {
          missingWeight = true;
          continue;
        }
        configuredWeightTotal += influence;
        const nextVisiting = new Set(visiting);
        nextVisiting.add(nodeId);
        const childResult = computeScore(edge.source, nextVisiting);
        const childScore = childResult.score ?? getScoreFromRatingActual(childResult.colorActual);
        if (childScore === null) {
          missingChildScore = true;
          continue;
        }
        weightedSum += childScore * influence;
        validWeightTotal += influence;
        if (typeof childResult.colorActual === "number") {
          weightedColorActualSum += childResult.colorActual * influence;
          validColorActualWeightTotal += influence;
        }
      }

      if (missingWeight) return { score: null, statusNote: "Child influence is incomplete" };
      if (Math.abs(configuredWeightTotal - 1) > 0.001) {
        return { score: null, statusNote: `Influence totals ${Math.round(configuredWeightTotal * 100)}%` };
      }
      if (missingChildScore || validWeightTotal === 0) {
        return { score: null, statusNote: "Waiting on child KPI performance" };
      }
      const score = weightedSum / validWeightTotal;
      return {
        score,
        target: node.target,
        actual: node.actual,
        colorActual:
          validColorActualWeightTotal > 0
            ? weightedColorActualSum / validColorActualWeightTotal
            : undefined,
      };
    };

    return baseNodes.map((node) => {
      const result = computeScore(node.id);
      const preserveRestrictedPerformance = Boolean(node.metricsRestricted);
      return {
        ...node,
        target: node.calculationMode === "calculated_values" ? result.target : node.target,
        actual:
          node.calculationMode === "calculated_values" ||
          node.calculationMode === "formula_percentage"
            ? result.actual
            : node.actual,
        status: preserveRestrictedPerformance ? node.status : getStatusFromScore(result.score),
        statusNote: preserveRestrictedPerformance ? undefined : result.statusNote ?? node.statusNote,
        colorActual: preserveRestrictedPerformance ? node.colorActual : result.colorActual,
      };
    });
  }, [aggregationMode, comparisonMode, referenceDate, workspace]);

  const ownerOwnedKpiIds = React.useMemo(
    () =>
      dashboardNodes
        .filter((node) => node.owner === selectedOwner)
        .map((node) => node.id),
    [dashboardNodes, selectedOwner]
  );

  const trackedKpiIds = React.useMemo(() => {
    if (!selectedOwner) return [];
    const hasSavedConfig = Boolean(
      workspace?.dashboardConfigs &&
        Object.prototype.hasOwnProperty.call(workspace.dashboardConfigs, selectedOwner)
    );
    if (hasSavedConfig) {
      const currentNodeIds = new Set(dashboardNodes.map((node) => node.id));
      const currentNodeIdsByLabel = new Map(dashboardNodes.map((node) => [node.label, node.id]));
      const savedConfig = workspace?.dashboardConfigs?.[selectedOwner];
      const validIds = (savedConfig?.trackedKpiIds ?? []).filter((id) => currentNodeIds.has(id));
      const labelResolvedIds = (savedConfig?.trackedKpiLabels ?? [])
        .map((label) => currentNodeIdsByLabel.get(label))
        .filter((id): id is string => typeof id === "string" && !validIds.includes(id));
      return [...validIds, ...labelResolvedIds];
    }
    return ownerOwnedKpiIds;
  }, [dashboardNodes, ownerOwnedKpiIds, selectedOwner, workspace?.dashboardConfigs]);

  const trackedSet = React.useMemo(() => new Set(trackedKpiIds), [trackedKpiIds]);
  const expandedParentSet = React.useMemo(() => new Set(expandedParentIds), [expandedParentIds]);

  const dashboardKpiRelationships = React.useMemo(() => {
    const parentByChild = new Map<string, string>();
    const labelsById = new Map(
      (workspace?.allNodes ?? []).map((node) => [node.id, node.data.label ?? "Untitled KPI"])
    );

    (workspace?.allEdges ?? []).forEach((edge) => {
      if (!parentByChild.has(edge.source)) {
        parentByChild.set(edge.source, edge.target);
      }
    });

    const getLineageLabels = (nodeId: string) => {
      const lineage: string[] = [];
      const visited = new Set<string>();
      let currentId: string | undefined = nodeId;

      while (currentId && !visited.has(currentId)) {
        visited.add(currentId);
        lineage.unshift(labelsById.get(currentId) ?? "Untitled KPI");
        currentId = parentByChild.get(currentId);
      }

      return lineage;
    };

    const lineageLabelsById = new Map(
      (workspace?.allNodes ?? []).map((node) => [node.id, getLineageLabels(node.id)])
    );
    const lineageLabelById = new Map(
      Array.from(lineageLabelsById.entries()).map(([nodeId, labels]) => [
        nodeId,
        labels.join(" > "),
      ])
    );

    return { lineageLabelsById, lineageLabelById };
  }, [workspace?.allEdges, workspace?.allNodes]);

  const dashboardCards = React.useMemo(
    () => {
      const nodesById = new Map(dashboardNodes.map((node) => [node.id, node]));
      const orderedTrackedNodes = trackedKpiIds
        .map((id) => nodesById.get(id))
        .filter((node): node is DashboardNode => Boolean(node));
      const fallbackTrackedNodes = dashboardNodes
        .filter((node) => trackedSet.has(node.id) && !trackedKpiIds.includes(node.id))
        .sort((left, right) => left.label.localeCompare(right.label));
      return [...orderedTrackedNodes, ...fallbackTrackedNodes];
    },
    [dashboardNodes, trackedKpiIds, trackedSet]
  );

  const dashboardDisplayCards = React.useMemo<DashboardDisplayCard[]>(() => {
    const cardsById = new Map(dashboardCards.map((node) => [node.id, node]));
    const trackedOrder = new Map(trackedKpiIds.map((id, index) => [id, index]));
    const trackedEdges = (workspace?.allEdges ?? []).filter(
      (edge) => trackedSet.has(edge.source) && trackedSet.has(edge.target)
    );

    const childMap = new Map<string, string[]>();
    trackedEdges.forEach((edge) => {
      if (!childMap.has(edge.target)) childMap.set(edge.target, []);
      childMap.get(edge.target)!.push(edge.source);
    });

    childMap.forEach((childIds, parentId) => {
      childIds.sort((left, right) => (trackedOrder.get(left) ?? 0) - (trackedOrder.get(right) ?? 0));
      childMap.set(parentId, Array.from(new Set(childIds)));
    });

    const childIdsWithTrackedParent = new Set<string>();
    childMap.forEach((childIds) => {
      childIds.forEach((childId) => childIdsWithTrackedParent.add(childId));
    });

    const rootIds = dashboardCards
      .map((node) => node.id)
      .filter((id) => !childIdsWithTrackedParent.has(id));

    const display: DashboardDisplayCard[] = [];
    const visited = new Set<string>();

    const appendNode = (
      nodeId: string,
      depth: number,
      hasTrackedParent: boolean,
      parentId?: string,
      parentLabel?: string,
      isLastChild?: boolean
    ) => {
      if (visited.has(nodeId)) return;
      const node = cardsById.get(nodeId);
      if (!node) return;
      visited.add(nodeId);
      const childIds = childMap.get(nodeId) ?? [];
      display.push({
        node,
        depth,
        childCount: childIds.length,
        hasTrackedParent,
        parentId,
        parentLabel,
        isLastChild,
      });
      if (!expandedParentSet.has(nodeId)) return;
      childIds.forEach((childId, index) =>
        appendNode(
          childId,
          depth + 1,
          true,
          nodeId,
          node.label,
          index === childIds.length - 1
        )
      );
    };

    rootIds.forEach((nodeId) => appendNode(nodeId, 0, false));
    dashboardCards.forEach((node) => {
      if (!visited.has(node.id) && !childIdsWithTrackedParent.has(node.id)) {
        appendNode(node.id, 0, false);
      }
    });

    return display;
  }, [dashboardCards, expandedParentSet, trackedKpiIds, trackedSet, workspace?.allEdges]);

  const dashboardGroups = React.useMemo(() => {
    const groups: DashboardDisplayCard[][] = [];
    let currentGroup: DashboardDisplayCard[] = [];

    dashboardDisplayCards.forEach((card) => {
      if (card.depth === 0) {
        if (currentGroup.length > 0) groups.push(currentGroup);
        currentGroup = [card];
        return;
      }
      if (currentGroup.length === 0) {
        currentGroup = [card];
        return;
      }
      currentGroup.push(card);
    });

    if (currentGroup.length > 0) groups.push(currentGroup);
    return groups;
  }, [dashboardDisplayCards]);

  const searchableKpis = React.useMemo(
    () =>
      dashboardNodes
        .filter((node) => {
          const lineageLabel = dashboardKpiRelationships.lineageLabelById.get(node.id) ?? node.label;
          return [node.label, lineageLabel, node.owner, formatValueTypeLabel(node.valueType), node.frequency]
            .join(" ")
            .toLowerCase()
            .includes(searchTerm.trim().toLowerCase());
        })
        .sort((left, right) => {
          const leftLineage = dashboardKpiRelationships.lineageLabelById.get(left.id) ?? left.label;
          const rightLineage = dashboardKpiRelationships.lineageLabelById.get(right.id) ?? right.label;
          return leftLineage.localeCompare(rightLineage);
        }),
    [dashboardKpiRelationships.lineageLabelById, dashboardNodes, searchTerm]
  );

  const persistWorkspace = React.useCallback(
    async (
      nextWorkspace: Workspace,
      enterpriseId: number,
      enterprise: EnterpriseSummary | null | undefined
    ) => {
      const workspaceToPersist = {
        ...nextWorkspace,
        enterpriseIdentity: getWorkspaceEnterpriseIdentity(enterpriseId, enterprise),
      };
      setIsSaving(true);
      try {
        await api.put(`/enterprises/${enterpriseId}/workspace`, {
          workspace: workspaceToPersist,
        });
        window.localStorage.setItem(
          getWorkspaceCacheKey(enterpriseId, enterprise),
          JSON.stringify(workspaceToPersist)
        );
        notifyWorkspaceChanged(enterpriseId, "workspace-save");
      } catch (saveError) {
        console.error("Failed to save dashboard workspace", saveError);
      } finally {
        setIsSaving(false);
      }
    },
    []
  );

  const updateWorkspaceNode = React.useCallback(
    (nodeId: string, updater: (node: PersistedNode) => PersistedNode) => {
      if (!workspace || selectedEnterpriseId === null) return;
      const nextWorkspace = {
        ...workspace,
        enterpriseIdentity: getWorkspaceEnterpriseIdentity(selectedEnterpriseId, selectedEnterprise),
        allNodes: (workspace.allNodes ?? []).map((node) => (node.id === nodeId ? updater(node) : node)),
      };
      setWorkspace(nextWorkspace);
      window.localStorage.setItem(
        getWorkspaceCacheKey(selectedEnterpriseId, selectedEnterprise),
        JSON.stringify(nextWorkspace)
      );
      void persistWorkspace(nextWorkspace, selectedEnterpriseId, selectedEnterprise);
    },
    [persistWorkspace, selectedEnterprise, selectedEnterpriseId, workspace]
  );

  const onKpiFieldChange = React.useCallback(
    (nodeId: string, field: "label" | "owner", value: string) => {
      updateWorkspaceNode(nodeId, (node) => ({
        ...node,
        data: { ...node.data, [field]: value },
      }));
    },
    [updateWorkspaceNode]
  );

  const onKpiMetricChange = React.useCallback(
    (nodeId: string, field: "target" | "actual", value: string) => {
      updateWorkspaceNode(nodeId, (node) => {
        const valueType = getNormalizedValueType(node.data.dataSource, node.data.valueType);
        let nextValue = value === "" ? undefined : Number(value);
        if (typeof nextValue === "number" && !Number.isNaN(nextValue) && valueType === "rating_1_10") {
          nextValue = clampRatingValue(nextValue);
        }
        const historyField = field === "target" ? "targetHistory" : "actualHistory";
        return {
          ...node,
          data: {
            ...node.data,
            [historyField]: setValueForContext(
              node.data[historyField] ?? {},
              referenceDate,
              node.data.frequency ?? "weekly",
              "global",
              defaultCampus,
              typeof nextValue === "number" && !Number.isNaN(nextValue) ? nextValue : undefined
            ),
          },
        };
      });
    },
    [referenceDate, updateWorkspaceNode]
  );

  const onKpiDetailChange = React.useCallback(
    (
      nodeId: string,
      field:
        | "frequency"
        | "definition"
        | "measurement"
        | "updateNotes"
        | "eventDate"
        | "dataSource"
        | "valueType"
        | "formulaNumeratorKpiId"
        | "formulaDenominatorKpiId",
      value: string
    ) => {
      updateWorkspaceNode(nodeId, (node) => ({
        ...node,
        data: {
          ...node.data,
          [field]: value,
          ...(field === "dataSource" && value === "Poll" ? { valueType: "rating_1_10" } : {}),
          ...(field === "valueType" &&
          normalizeCalculationMode(node.data.calculationMode, node.data.isRollup) ===
            "formula_percentage"
            ? { valueType: "percentage" }
            : {}),
        },
      }));
    },
    [updateWorkspaceNode]
  );

  const onKpiConfigChange = React.useCallback(
    (
      nodeId: string,
      field: "performanceDirection" | "calculationMode",
      value: string
    ) => {
      updateWorkspaceNode(nodeId, (node) => ({
        ...node,
        data: {
          ...node.data,
          [field]: value,
          ...(field === "calculationMode"
            ? {
                isRollup: value !== "direct",
                ...(value === "formula_percentage" ? { valueType: "percentage" } : {}),
              }
            : {}),
        },
      }));
    },
    [updateWorkspaceNode]
  );

  const onCreatePoll = React.useCallback(
    (nodeId: string) => {
      updateWorkspaceNode(nodeId, (node) => ({
        ...node,
        data: {
          ...node.data,
          unit: "",
          dataSource: "Poll",
          valueType: "rating_1_10",
          pollConfig:
            node.data.pollConfig ??
            createPollConfig({
              label: node.data.label,
              definition: node.data.definition,
              measurement: node.data.measurement,
            }),
        },
      }));
    },
    [updateWorkspaceNode]
  );

  const onTaskChange = React.useCallback(
    (nodeId: string, taskId: string, field: keyof Omit<TaskItem, "id">, value: string) => {
      updateWorkspaceNode(nodeId, (node) => ({
        ...node,
        data: {
          ...node.data,
          tasks: (node.data.tasks ?? []).map((task) =>
            task.id === taskId ? { ...task, [field]: value } : task
          ),
        },
      }));
    },
    [updateWorkspaceNode]
  );

  const onAddTask = React.useCallback(
    (nodeId: string) => {
      updateWorkspaceNode(nodeId, (node) => ({
        ...node,
        data: {
          ...node.data,
          tasks: [...(node.data.tasks ?? []), createTask()],
        },
      }));
    },
    [updateWorkspaceNode]
  );

  const onDeleteTask = React.useCallback(
    (nodeId: string, taskId: string) => {
      updateWorkspaceNode(nodeId, (node) => ({
        ...node,
        data: {
          ...node.data,
          tasks: (node.data.tasks ?? []).filter((task) => task.id !== taskId),
        },
      }));
    },
    [updateWorkspaceNode]
  );

  const onInitiativeChange = React.useCallback(
    (
      nodeId: string,
      initiativeId: string,
      field: keyof Omit<InitiativeItem, "id" | "tasks">,
      value: string
    ) => {
      updateWorkspaceNode(nodeId, (node) => ({
        ...node,
        data: {
          ...node.data,
          initiatives: (node.data.initiatives ?? []).map((initiative) =>
            initiative.id === initiativeId ? { ...initiative, [field]: value } : initiative
          ),
        },
      }));
    },
    [updateWorkspaceNode]
  );

  const onAddInitiative = React.useCallback(
    (nodeId: string) => {
      updateWorkspaceNode(nodeId, (node) => ({
        ...node,
        data: {
          ...node.data,
          initiatives: [...(node.data.initiatives ?? []), createInitiative()],
        },
      }));
    },
    [updateWorkspaceNode]
  );

  const onDeleteInitiative = React.useCallback(
    (nodeId: string, initiativeId: string) => {
      updateWorkspaceNode(nodeId, (node) => ({
        ...node,
        data: {
          ...node.data,
          initiatives: (node.data.initiatives ?? []).filter(
            (initiative) => initiative.id !== initiativeId
          ),
        },
      }));
    },
    [updateWorkspaceNode]
  );

  const onInitiativeTaskChange = React.useCallback(
    (
      nodeId: string,
      initiativeId: string,
      taskId: string,
      field: keyof Omit<TaskItem, "id">,
      value: string
    ) => {
      updateWorkspaceNode(nodeId, (node) => ({
        ...node,
        data: {
          ...node.data,
          initiatives: (node.data.initiatives ?? []).map((initiative) =>
            initiative.id !== initiativeId
              ? initiative
              : {
                  ...initiative,
                  tasks: (initiative.tasks ?? []).map((task) =>
                    task.id === taskId ? { ...task, [field]: value } : task
                  ),
                }
          ),
        },
      }));
    },
    [updateWorkspaceNode]
  );

  const onAddInitiativeTask = React.useCallback(
    (nodeId: string, initiativeId: string) => {
      updateWorkspaceNode(nodeId, (node) => ({
        ...node,
        data: {
          ...node.data,
          initiatives: (node.data.initiatives ?? []).map((initiative) =>
            initiative.id === initiativeId
              ? { ...initiative, tasks: [...(initiative.tasks ?? []), createTask()] }
              : initiative
          ),
        },
      }));
    },
    [updateWorkspaceNode]
  );

  const onDeleteInitiativeTask = React.useCallback(
    (nodeId: string, initiativeId: string, taskId: string) => {
      updateWorkspaceNode(nodeId, (node) => ({
        ...node,
        data: {
          ...node.data,
          initiatives: (node.data.initiatives ?? []).map((initiative) =>
            initiative.id === initiativeId
              ? {
                  ...initiative,
                  tasks: (initiative.tasks ?? []).filter((task) => task.id !== taskId),
                }
              : initiative
          ),
        },
      }));
    },
    [updateWorkspaceNode]
  );

  const updateDashboardConfig = React.useCallback(
    (updater: (current: DashboardConfig) => DashboardConfig) => {
      if (!selectedOwner || !workspace || selectedEnterpriseId === null) return;
      const currentConfig: DashboardConfig = workspace.dashboardConfigs?.[selectedOwner] ?? {
        trackedKpiIds: ownerOwnedKpiIds,
        trackedKpiLabels: ownerOwnedKpiIds
          .map((id) => workspace.allNodes?.find((node) => node.id === id)?.data.label)
          .filter((label): label is string => typeof label === "string"),
      };
      const updatedConfig = updater(currentConfig);
      const trackedKpiLabels = updatedConfig.trackedKpiIds
        .map((id) => workspace.allNodes?.find((node) => node.id === id)?.data.label)
        .filter((label): label is string => typeof label === "string");
      const nextWorkspace = {
        ...workspace,
        enterpriseIdentity: getWorkspaceEnterpriseIdentity(selectedEnterpriseId, selectedEnterprise),
        dashboardConfigs: {
          ...(workspace.dashboardConfigs ?? {}),
          [selectedOwner]: {
            ...updatedConfig,
            trackedKpiLabels,
          },
        },
      };
      setWorkspace(nextWorkspace);
      window.localStorage.setItem(
        getWorkspaceCacheKey(selectedEnterpriseId, selectedEnterprise),
        JSON.stringify(nextWorkspace)
      );
      void persistWorkspace(nextWorkspace, selectedEnterpriseId, selectedEnterprise);
    },
    [ownerOwnedKpiIds, persistWorkspace, selectedEnterprise, selectedEnterpriseId, selectedOwner, workspace]
  );

  const toggleTrackedKpi = React.useCallback(
    (kpiId: string) => {
      updateDashboardConfig((current) => {
        const isSelected = current.trackedKpiIds.includes(kpiId);
        return {
          trackedKpiIds: isSelected
            ? current.trackedKpiIds.filter((id) => id !== kpiId)
            : [...current.trackedKpiIds, kpiId],
        };
      });
    },
    [updateDashboardConfig]
  );

  const addTrackedKpi = React.useCallback(
    (kpiId: string) => {
      updateDashboardConfig((current) => {
        if (current.trackedKpiIds.includes(kpiId)) return current;
        return {
          ...current,
          trackedKpiIds: [...current.trackedKpiIds, kpiId],
        };
      });
    },
    [updateDashboardConfig]
  );

  const placeTrackedKpi = React.useCallback(
    (sourceId: string, targetId: string, position: DashboardInsertPosition) => {
      if (sourceId === targetId) return;
      updateDashboardConfig((current) => {
        const wasTracked = current.trackedKpiIds.includes(sourceId);
        const nextIds = current.trackedKpiIds.filter((id) => id !== sourceId);
        const targetIndex = nextIds.indexOf(targetId);
        if (targetIndex === -1) {
          return current;
        }
        nextIds.splice(position === "after" ? targetIndex + 1 : targetIndex, 0, sourceId);
        if (wasTracked && nextIds.join("|") === current.trackedKpiIds.join("|")) {
          return current;
        }
        return {
          ...current,
          trackedKpiIds: nextIds,
        };
      });
    },
    [updateDashboardConfig]
  );

  const onDashboardLibraryDrop = React.useCallback(
    (event: React.DragEvent<HTMLElement>) => {
      const kpiId =
        event.dataTransfer.getData("application/x-lenses-dashboard-kpi") ||
        event.dataTransfer.getData("text/plain");
      if (!kpiId) return;
      event.preventDefault();
      event.stopPropagation();
      addTrackedKpi(kpiId);
      setIsCustomizeOpen(false);
    },
    [addTrackedKpi]
  );

  const toggleParentExpansion = React.useCallback((nodeId: string) => {
    setExpandedParentIds((current) =>
      current.includes(nodeId) ? current.filter((id) => id !== nodeId) : [...current, nodeId]
    );
  }, []);

  React.useEffect(() => {
    if (!workspace || selectedEnterpriseId === null) return;
    if (skipNextWorkspaceAutosaveRef.current) {
      skipNextWorkspaceAutosaveRef.current = false;
      return;
    }
    const timeoutId = window.setTimeout(async () => {
      setIsSaving(true);
      try {
        await api.put(`/enterprises/${selectedEnterpriseId}/workspace`, {
          workspace: {
            ...workspace,
            enterpriseIdentity: getWorkspaceEnterpriseIdentity(selectedEnterpriseId, selectedEnterprise),
          },
        });
        const workspaceToCache = {
          ...workspace,
          enterpriseIdentity: getWorkspaceEnterpriseIdentity(selectedEnterpriseId, selectedEnterprise),
        };
        window.localStorage.setItem(
          getWorkspaceCacheKey(selectedEnterpriseId, selectedEnterprise),
          JSON.stringify(workspaceToCache)
        );
        notifyWorkspaceChanged(selectedEnterpriseId, "workspace-save");
      } catch (saveError) {
        console.error("Failed to save dashboard workspace", saveError);
      } finally {
        setIsSaving(false);
      }
    }, 500);
    return () => window.clearTimeout(timeoutId);
  }, [selectedEnterprise, selectedEnterpriseId, workspace]);

  if (isLoading && !workspace) {
    return <div style={{ padding: 24 }}>Loading dashboard...</div>;
  }

  const dashboardChromeButton: React.CSSProperties = {
    boxSizing: "border-box",
    height: 34,
    border: "1px solid rgba(148,163,184,0.26)",
    borderRadius: 8,
    padding: "0 12px",
    color: "#cbd5e1",
    background: "rgba(15,23,42,0.72)",
    fontSize: 13,
    fontWeight: 800,
    textDecoration: "none",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    whiteSpace: "nowrap",
    cursor: "pointer",
  };
  const activeDashboardChromeButton: React.CSSProperties = {
    ...dashboardChromeButton,
    borderColor: "rgba(34,197,94,0.55)",
    color: "#f8fafc",
    background: "rgba(34,197,94,0.18)",
    boxShadow: "0 0 22px rgba(34,197,94,0.18), inset 0 1px 0 rgba(255,255,255,0.05)",
  };
  const dashboardAccountPill = signedInUser ? (
    <div
      aria-label="Signed in account"
      style={{
        minHeight: 34,
        display: "flex",
        alignItems: "center",
        gap: 8,
        maxWidth: 300,
        minWidth: 0,
        border: "1px solid rgba(148,163,184,0.26)",
        borderRadius: 10,
        padding: "5px 6px 5px 10px",
        background: "rgba(8,13,20,0.82)",
        color: "#f8fafc",
      }}
    >
      <div style={{ minWidth: 0 }}>
        <div
          style={{
            maxWidth: 150,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            fontSize: 12,
            fontWeight: 900,
            lineHeight: 1.1,
          }}
        >
          {signedInUser.name || "Signed in"}
        </div>
        <div
          style={{
            maxWidth: 150,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            fontSize: 10,
            color: "#9ca3af",
            lineHeight: 1.1,
          }}
        >
          {signedInUser.email || signedInUser.role || ""}
        </div>
      </div>
      {onSignOut ? (
        <button
          type="button"
          onClick={onSignOut}
          style={{
            border: "1px solid rgba(148,163,184,0.38)",
            borderRadius: 8,
            background: "rgba(15,23,42,0.82)",
            color: "#e5e7eb",
            padding: "6px 8px",
            fontSize: 12,
            fontWeight: 900,
            whiteSpace: "nowrap",
            cursor: "pointer",
          }}
        >
          Sign out
        </button>
      ) : null}
    </div>
  ) : null;
  const dashboardViewSwitcher = (
    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
      <button
        type="button"
        onClick={() => setIsCustomizeOpen((current) => !current)}
        style={{
          ...dashboardChromeButton,
          borderColor: isCustomizeOpen ? "#2563eb" : "rgba(203,213,225,0.9)",
          color: isCustomizeOpen ? "#1d4ed8" : "#334155",
          background: isCustomizeOpen ? "#eff6ff" : "rgba(255,255,255,0.72)",
        }}
      >
        Settings
      </button>
      {dashboardAccountPill}
      <div style={{ display: "grid", gap: 4 }}>
        <div
          style={{
            fontSize: 11,
            fontWeight: 800,
            color: "#9ca3af",
            textTransform: "uppercase",
            textAlign: "center",
          }}
        >
          Views
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 3,
            padding: 3,
            borderRadius: 10,
            border: "1px solid rgba(148,163,184,0.26)",
            background: "rgba(8,13,20,0.82)",
          }}
          aria-label="Workspace views"
        >
          <Link to="/" style={{ ...dashboardChromeButton, borderColor: "transparent", background: "transparent", color: "#e5e7eb" }}>
            Map
          </Link>
          <Link
            to={selectedEnterpriseId ? `/reports?enterpriseId=${selectedEnterpriseId}` : "/reports"}
            style={{ ...dashboardChromeButton, borderColor: "transparent", background: "transparent", color: "#e5e7eb" }}
          >
            Summary
          </Link>
          <span style={activeDashboardChromeButton}>Dashboard</span>
        </div>
      </div>
    </div>
  );

  return (
    <div
      className="map-designed-page dashboard-view"
      style={{
        minHeight: "100vh",
        background: "#05070b",
        color: "#f8fafc",
        padding: 16,
      }}
    >
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr",
          gap: 16,
          alignItems: "start",
          marginBottom: 18,
        }}
      >
        <div
          style={{
            background: "linear-gradient(180deg, rgba(15,23,42,0.96) 0%, rgba(8,13,20,0.94) 100%)",
            border: "1px solid rgba(148,163,184,0.24)",
            borderRadius: 24,
            padding: 16,
            boxShadow:
              "0 18px 44px rgba(0,0,0,0.36), 0 0 46px rgba(34,197,94,0.08), inset 0 1px 0 rgba(255,255,255,0.06)",
            backdropFilter: "blur(10px)",
            display: "grid",
            gap: 12,
            alignItems: "start",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 16,
              flexWrap: "wrap",
              padding: "2px 4px 2px 0",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
              <img
                src={brandLogoUrl}
                alt="Lenses"
                style={{ width: 60, height: 60, objectFit: "contain", clipPath: "inset(3px 0 0 0)" }}
              />
              <div>
                <div
                  style={{
                    fontSize: 12,
                    fontWeight: 800,
                    color: "#22c55e",
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                    lineHeight: 1.1,
                  }}
                >
                  Lenses
                </div>
                <div style={{ fontSize: 16, fontWeight: 800, color: "#f8fafc", lineHeight: 1.15 }}>
                  Dashboard View
                </div>
              </div>
            </div>
            {dashboardViewSwitcher}
          </div>

          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-start" }}>
            <div style={{ display: "grid", gap: 4, justifyItems: "center" }}>
              <div style={{ fontSize: 11, fontWeight: 800, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                Enterprise
              </div>
              <select
                value={selectedEnterpriseId ?? ""}
                onChange={(event) => {
                  const nextEnterpriseId = Number(event.target.value);
                  setSelectedEnterpriseId(nextEnterpriseId);
                  setSearchParams((current) => {
                    current.set("enterpriseId", String(nextEnterpriseId));
                    return current;
                  });
                }}
                style={{ border: "1px solid rgba(148,163,184,0.28)", borderRadius: 8, padding: "8px 10px", fontSize: 14, minWidth: 180, background: "rgba(8,13,20,0.92)", color: "#f8fafc" }}
              >
                {enterprises.map((enterprise) => (
                  <option key={enterprise.id} value={enterprise.id}>
                    {enterprise.name}
                  </option>
                ))}
              </select>
            </div>

            <div style={{ display: "grid", gap: 4, justifyItems: "center" }}>
              <div style={{ fontSize: 11, fontWeight: 800, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                Dashboard For
              </div>
              <select
                value={selectedOwner}
                onChange={(event) => setSelectedOwner(event.target.value)}
                style={{ border: "1px solid rgba(148,163,184,0.28)", borderRadius: 8, padding: "8px 10px", fontSize: 14, minWidth: 220, background: "rgba(8,13,20,0.92)", color: "#f8fafc" }}
              >
                {ownerOptions.map((owner) => (
                  <option key={owner} value={owner}>
                    {owner}
                  </option>
                ))}
              </select>
            </div>

            <div style={{ display: "grid", gap: 4, justifyItems: "center" }}>
              <div style={{ fontSize: 11, fontWeight: 800, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                Date
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "40px 1fr 40px", gap: 8, minWidth: 220 }}>
                <button
                  type="button"
                  onClick={() => setReferenceDate((current) => shiftReferenceDate(current, "previous"))}
                  style={{ border: "1px solid rgba(148,163,184,0.28)", borderRadius: 8, background: "rgba(8,13,20,0.92)", color: "#f8fafc", fontWeight: 800 }}
                >
                  {"<"}
                </button>
                <div
                  style={{
                    border: "1px solid rgba(148,163,184,0.28)",
                    borderRadius: 8,
                    padding: "8px 10px",
                    background: "rgba(15,23,42,0.86)",
                    color: "#f8fafc",
                    fontSize: 14,
                    fontWeight: 800,
                    textAlign: "center",
                  }}
                >
                  {formatReferenceDate(referenceDate, weekStartsOn)}
                </div>
                <button
                  type="button"
                  onClick={() => setReferenceDate((current) => shiftReferenceDate(current, "next"))}
                  style={{ border: "1px solid rgba(148,163,184,0.28)", borderRadius: 8, background: "rgba(8,13,20,0.92)", color: "#f8fafc", fontWeight: 800 }}
                >
                  {">"}
                </button>
              </div>
            </div>

            <div style={{ display: "grid", gap: 4, justifyItems: "center" }}>
              <div style={{ fontSize: 11, fontWeight: 800, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                Aggregation
              </div>
              <select
                value={aggregationMode}
                onChange={(event) => setAggregationMode(event.target.value as AggregationMode)}
                style={{ border: "1px solid rgba(148,163,184,0.28)", borderRadius: 8, padding: "8px 10px", fontSize: 14, minWidth: 170, background: "rgba(8,13,20,0.92)", color: "#f8fafc" }}
              >
                <option value="current">Current Period</option>
                <option value="mtd">MTD</option>
                <option value="qtd">QTD</option>
                <option value="ytd">YTD</option>
              </select>
            </div>

            <div style={{ display: "grid", gap: 4, justifyItems: "center" }}>
              <div style={{ fontSize: 11, fontWeight: 800, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                Comparison
              </div>
              <select
                value={comparisonMode}
                onChange={(event) => setComparisonMode(event.target.value as ComparisonMode)}
                style={{ border: "1px solid rgba(148,163,184,0.28)", borderRadius: 8, padding: "8px 10px", fontSize: 14, minWidth: 180, background: "rgba(8,13,20,0.92)", color: "#f8fafc" }}
              >
                {comparisonModeOptions.map((option) => (
                  <option key={option} value={option}>
                    {formatComparisonLabel(option)}
                  </option>
                ))}
              </select>
            </div>

          </div>
        </div>
      </div>

      {isCustomizeOpen ? (
        <div
          onClick={() => setIsCustomizeOpen(false)}
          onDragOver={(event) => {
            if (event.dataTransfer.types.includes("application/x-lenses-dashboard-kpi")) {
              event.preventDefault();
            }
          }}
          onDrop={onDashboardLibraryDrop}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(17, 24, 39, 0.52)",
            display: "grid",
            placeItems: "center",
            padding: 24,
            zIndex: 1000,
          }}
        >
          <div
            className="settings-library-surface"
            onClick={(event) => event.stopPropagation()}
            style={{
              width: "min(920px, 100%)",
              maxHeight: "min(82vh, 780px)",
              overflow: "auto",
              background: "#fff",
              borderRadius: 18,
              border: "1px solid #d1d5db",
              boxShadow: "0 18px 40px rgba(0,0,0,0.35)",
              padding: 22,
              display: "grid",
              gap: 16,
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                gap: 16,
                position: "sticky",
                top: -22,
                margin: "-22px -22px 0",
                padding: "22px 22px 16px",
                background: "rgba(255,255,255,0.96)",
                borderBottom: "1px solid #e5e7eb",
                zIndex: 2,
                backdropFilter: "blur(6px)",
              }}
            >
              <div>
                <div style={{ fontSize: 20, fontWeight: 800, color: "#111827" }}>
                  Settings & Libraries
                </div>
                <div style={{ fontSize: 13, color: "#4b5563", marginTop: 4 }}>
                  Access workspace libraries and manage the KPIs shown for {selectedOwner || "this dashboard"}.
                </div>
              </div>
              <button
                type="button"
                onClick={() => setIsCustomizeOpen(false)}
                style={{
                  border: "1px solid #d1d5db",
                  borderRadius: 10,
                  padding: "8px 12px",
                  background: "#fff",
                  color: "#111827",
                  fontWeight: 700,
                  cursor: "pointer",
                }}
              >
                Close
              </button>
            </div>

            <div style={{ fontSize: 13, color: "#4b5563", lineHeight: 1.5 }}>
              Open only the areas you want to work in.
            </div>

            <div style={{ display: "grid", gap: 10 }}>
              <button
                type="button"
                onClick={() => setIsSettingsLensesCollapsed((previous) => !previous)}
                style={{
                  width: "100%",
                  border: "1px solid #d1d5db",
                  borderRadius: 12,
                  padding: "12px 14px",
                  fontSize: 14,
                  fontWeight: 800,
                  background: "#f9fafb",
                  color: "#111827",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <span>Lenses</span>
                <span>{isSettingsLensesCollapsed ? "Show" : "Hide"}</span>
              </button>
              {isSettingsLensesCollapsed ? null : (
                <div style={{ marginLeft: 16, display: "grid", gap: 10 }}>
                  <Link
                    to={selectedEnterpriseId ? `/?enterpriseId=${selectedEnterpriseId}` : "/"}
                    style={{
                      border: "1px solid #d1d5db",
                      borderRadius: 12,
                      padding: "10px 12px",
                      background: "#111827",
                      color: "#fff",
                      fontSize: 13,
                      fontWeight: 700,
                      textDecoration: "none",
                      textAlign: "center",
                    }}
                  >
                    Manage Lenses
                  </Link>
                  {workspaceLenses.map((lens) => (
                    <div
                      key={lens}
                      style={{
                        border: "1px solid #d1d5db",
                        borderRadius: 14,
                        padding: 12,
                        background: "#fff",
                      }}
                    >
                      <div style={{ fontSize: 15, fontWeight: 700, color: "#111827" }}>
                        {workspaceLensConfigs[lens]?.name ?? lens}
                      </div>
                      <div style={{ fontSize: 12, color: "#4b5563", marginTop: 4 }}>
                        {(workspace?.allNodes ?? []).filter((node) => node.data.lenses?.includes(lens)).length} KPI
                        {(workspace?.allNodes ?? []).filter((node) => node.data.lenses?.includes(lens)).length === 1 ? "" : "s"} in this lens
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <button
                type="button"
                onClick={() => setIsSettingsKpisCollapsed((previous) => !previous)}
                style={{
                  width: "100%",
                  border: "1px solid #d1d5db",
                  borderRadius: 12,
                  padding: "12px 14px",
                  fontSize: 14,
                  fontWeight: 800,
                  background: "#f9fafb",
                  color: "#111827",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <span>KPIs</span>
                <span>{isSettingsKpisCollapsed ? "Show" : "Hide"}</span>
              </button>
              {isSettingsKpisCollapsed ? null : (
                <div style={{ marginLeft: 16, display: "grid", gap: 10 }}>
                  <div style={{ fontSize: 13, color: "#4b5563", lineHeight: 1.5 }}>
                    Drag a KPI onto the dashboard to add it for {selectedOwner || "this dashboard"}.
                  </div>
                  <input
            type="search"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            placeholder="Search KPIs to add or remove..."
            style={{
              width: "100%",
              boxSizing: "border-box",
              border: "1px solid #d1d5db",
              borderRadius: 12,
              padding: "10px 12px",
              fontSize: 14,
            }}
          />
                  <div style={{ display: "grid", gap: 10 }}>
                    {searchableKpis.map((node) => {
                      const checked = trackedSet.has(node.id);
                      const owned = node.owner === selectedOwner;
                      const lineageLabels = dashboardKpiRelationships.lineageLabelsById.get(node.id) ?? [node.label];
                      const lineageLabel = dashboardKpiRelationships.lineageLabelById.get(node.id) ?? node.label;
                      const ancestorLabels = lineageLabels.slice(0, -1);
                      return (
                        <div
                          key={node.id}
                          draggable={!checked}
                          onDragStart={(event) => {
                            event.dataTransfer.effectAllowed = "copy";
                            event.dataTransfer.setData("application/x-lenses-dashboard-kpi", node.id);
                            event.dataTransfer.setData("text/plain", node.id);
                          }}
                          style={{
                            border: checked ? "2px solid #111827" : "1px solid #d1d5db",
                            borderRadius: 14,
                            padding: 12,
                            background: checked ? "#f3f4f6" : "#fff",
                            cursor: checked ? "default" : "grab",
                          }}
                        >
                          {ancestorLabels.length > 0 ? (
                            <div
                              title={lineageLabel}
                              style={{
                                fontSize: 11,
                                color: "#64748b",
                                fontWeight: 800,
                                lineHeight: 1.35,
                                marginBottom: 6,
                              }}
                            >
                              {ancestorLabels.join(" > ")}
                            </div>
                          ) : null}
                          <div style={{ fontSize: 15, fontWeight: 700, color: "#111827", marginBottom: 4 }}>
                            {node.label}
                          </div>
                          <div style={{ fontSize: 12, color: "#4b5563", lineHeight: 1.45, marginBottom: 8 }}>
                            {node.frequency} | {formatValueTypeLabel(node.valueType)}
                          </div>
                          <div style={{ fontSize: 12, color: "#4b5563", lineHeight: 1.45, marginBottom: 8 }}>
                            Owner: {owned ? "Owned by this person" : node.owner || "Unassigned"}
                          </div>
                          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                            <button
                              type="button"
                              onClick={() => toggleTrackedKpi(node.id)}
                              style={{
                                border: "1px solid #d1d5db",
                                borderRadius: 10,
                                padding: "8px 10px",
                                fontSize: 13,
                                fontWeight: 700,
                                background: checked ? "#fff" : "#111827",
                                color: checked ? "#111827" : "#fff",
                                cursor: "pointer",
                              }}
                            >
                              {checked ? "Remove" : "Add"}
                            </button>
                            <span style={{ alignSelf: "center", fontSize: 12, fontWeight: 700, color: checked ? "#374151" : "#1d4ed8" }}>
                              {checked ? "Already on dashboard" : "Drag onto dashboard"}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              <button
                type="button"
                onClick={() => setIsSettingsReferenceListsCollapsed((previous) => !previous)}
                style={{
                  width: "100%",
                  border: "1px solid #d1d5db",
                  borderRadius: 12,
                  padding: "12px 14px",
                  fontSize: 14,
                  fontWeight: 800,
                  background: "#f9fafb",
                  color: "#111827",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <span>Reference Lists</span>
                <span>{isSettingsReferenceListsCollapsed ? "Show" : "Hide"}</span>
              </button>
              {isSettingsReferenceListsCollapsed ? null : (
                <div style={{ marginLeft: 16, display: "grid", gap: 10 }}>
                  <Link
                    to={selectedEnterpriseId ? `/?enterpriseId=${selectedEnterpriseId}` : "/"}
                    style={{
                      border: "1px solid #d1d5db",
                      borderRadius: 12,
                      padding: "10px 12px",
                      background: "#111827",
                      color: "#fff",
                      fontSize: 13,
                      fontWeight: 700,
                      textDecoration: "none",
                      textAlign: "center",
                    }}
                  >
                    Manage Reference Lists
                  </Link>
                  <div style={{ border: "1px solid #d1d5db", borderRadius: 14, padding: 12, background: "#fff" }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "#111827" }}>Owners</div>
                    <div style={{ fontSize: 13, color: "#6b7280", marginTop: 4 }}>
                      {ownerOptions.length > 0 ? ownerOptions.join(", ") : "No owners listed."}
                    </div>
                  </div>
                  <div style={{ border: "1px solid #d1d5db", borderRadius: 14, padding: 12, background: "#fff" }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "#111827" }}>Poll Audience Groups</div>
                    <div style={{ fontSize: 13, color: "#6b7280", marginTop: 4 }}>
                      {(workspace?.pollAudienceGroups ?? []).length > 0
                        ? (workspace?.pollAudienceGroups ?? []).join(", ")
                        : "No audience groups listed."}
                    </div>
                  </div>
                  <div style={{ border: "1px solid #d1d5db", borderRadius: 14, padding: 12, background: "#fff" }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "#111827" }}>Poll Recipients</div>
                    <div style={{ fontSize: 13, color: "#6b7280", marginTop: 4 }}>
                      {(workspace?.pollRecipients ?? []).length} recipients in this enterprise
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      ) : null}

      {error ? (
        <div style={{ color: "#fca5a5", marginBottom: 16 }}>{error}</div>
      ) : null}

      <div
        ref={dashboardCanvasRef}
        onWheel={onDashboardCanvasWheel}
        onPointerDown={onDashboardCanvasPointerDown}
        onPointerMove={onDashboardCanvasPointerMove}
        onPointerUp={onDashboardCanvasPointerEnd}
        onPointerCancel={onDashboardCanvasPointerEnd}
        onDragOver={(event) => {
          if (event.dataTransfer.types.includes("application/x-lenses-dashboard-kpi")) {
            event.preventDefault();
            event.dataTransfer.dropEffect = "copy";
          }
        }}
        onDrop={onDashboardLibraryDrop}
        style={{
          height: "calc(100vh - 245px)",
          minHeight: 520,
          overflow: "hidden",
          position: "relative",
          borderRadius: 18,
          border: "1px solid rgba(31,41,55,0.7)",
          background:
            "radial-gradient(circle at 1px 1px, rgba(148,163,184,0.22) 1px, transparent 0), #05070b",
          backgroundSize: "22px 22px",
          cursor: dashboardPanDragRef.current ? "grabbing" : "grab",
          touchAction: "none",
        }}
      >
        <div
          ref={dashboardContentRef}
          style={{
            display: "grid",
            gridTemplateColumns: `repeat(auto-fit, minmax(${dashboardCardMinWidth}px, 1fr))`,
            gap: dashboardCardGap,
            transform: `translate(${dashboardPan.x}px, ${dashboardPan.y}px) scale(${dashboardZoom})`,
            transformOrigin: "top left",
            width: "min(1560px, calc(100vw - 48px))",
            padding: 18,
            position: "absolute",
            top: 0,
            left: 0,
            touchAction: "none",
          }}
        >
          {dashboardGroups.map((group) => (
            <div
              key={group[0]?.node.id ?? Math.random().toString(36)}
              style={{ display: "grid", gap: 10, alignContent: "start" }}
            >
            {group.map(({ node, depth, childCount, hasTrackedParent, parentId, parentLabel, isLastChild }) => {
          const trendPoints =
            node.calculationMode === "calculated_values" ||
            node.calculationMode === "formula_percentage"
              ? buildCalculatedTrendPoints(
                  node.id,
                  workspace?.allNodes ?? [],
                  workspace?.allEdges ?? [],
                  referenceDate
                )
              : buildTrendPoints(
                  node.actualHistory,
                  referenceDate,
                  node.frequency,
                  node.valueType
                );
          const scoreColor = kpiDisplayColor(node.valueType, node.actual, node.colorActual, node.status);
          const isExpanded = expandedParentSet.has(node.id);
          const canToggleChildren = childCount > 0;
          const cardIndent = depth * 28;
          const connectorOffset = Math.max(14, cardIndent - 14);
          const lineageLabels = dashboardKpiRelationships.lineageLabelsById.get(node.id) ?? [node.label];
          const lineageLabel = dashboardKpiRelationships.lineageLabelById.get(node.id) ?? node.label;
          const ancestorLabels = lineageLabels.slice(0, -1);

          return (
            <div
              data-dashboard-card="true"
              key={node.id}
              draggable={!hasTrackedParent}
              onDragStart={(event) => {
                if (hasTrackedParent) {
                  event.preventDefault();
                  return;
                }
                setDraggedDashboardKpiId(node.id);
                event.dataTransfer.effectAllowed = "move";
                event.dataTransfer.setData("application/x-lenses-dashboard-reorder", node.id);
                event.dataTransfer.setData("text/plain", node.id);
              }}
              onDragOver={(event) => {
                event.preventDefault();
                event.dataTransfer.dropEffect = "move";
              }}
              onDrop={(event) => {
                event.preventDefault();
                event.stopPropagation();
                const sourceId =
                  draggedDashboardKpiId ||
                  event.dataTransfer.getData("application/x-lenses-dashboard-reorder") ||
                  event.dataTransfer.getData("application/x-lenses-dashboard-kpi") ||
                  event.dataTransfer.getData("text/plain");
                const targetId = hasTrackedParent ? parentId ?? node.id : node.id;
                const rect = event.currentTarget.getBoundingClientRect();
                const position: DashboardInsertPosition =
                  event.clientY > rect.top + rect.height / 2 ? "after" : "before";
                if (sourceId) {
                  placeTrackedKpi(sourceId, targetId, position);
                }
                setDraggedDashboardKpiId(null);
              }}
              onDragEnd={() => setDraggedDashboardKpiId(null)}
              onDoubleClick={(event) => {
                event.stopPropagation();
                setIsKpiWorkOpen(false);
                setEditingKpiId(node.id);
              }}
              style={{
                borderRadius: 10,
                border:
                  draggedDashboardKpiId === node.id
                    ? "2px dashed #2563eb"
                    : `2px solid ${scoreColor}`,
                background: scoreColor,
                boxShadow: `0 10px 24px rgba(0,0,0,0.25), 0 0 24px ${hexToRgba(scoreColor, 0.32)}, 0 0 48px ${hexToRgba(scoreColor, 0.16)}`,
                overflow: "hidden",
                touchAction: "none",
                cursor: hasTrackedParent ? "default" : "grab",
                opacity: draggedDashboardKpiId === node.id ? 0.6 : 1,
                marginLeft: cardIndent,
                maxWidth: depth > 0 ? `calc(100% - ${cardIndent}px)` : undefined,
                position: "relative",
                padding: 8,
              }}
            >
              {hasTrackedParent ? (
                <>
                  <div
                    style={{
                      position: "absolute",
                      left: -connectorOffset,
                      top: -12,
                      bottom: isLastChild ? "50%" : -12,
                      width: 2,
                      background: "rgba(148,163,184,0.65)",
                      borderRadius: 999,
                    }}
                  />
                  <div
                    style={{
                      position: "absolute",
                      left: -connectorOffset,
                      top: "50%",
                      width: Math.max(12, connectorOffset - 8),
                      height: 2,
                      background: "rgba(148,163,184,0.65)",
                      borderRadius: 999,
                    }}
                  />
                </>
              ) : null}
              <div
                style={{
                  borderRadius: 10,
                  border: `1px solid ${hexToRgba(scoreColor, 0.82)}`,
                  background: "linear-gradient(180deg, rgba(17,24,39,0.98), #080d14)",
                  boxShadow: "inset 0 1px 0 rgba(255,255,255,0.05)",
                  overflow: "hidden",
                }}
              >
              <div style={{ padding: dashboardCardPadding, display: "grid", gap: 14 }}>
                <div>
                  {ancestorLabels.length > 0 ? (
                    <div
                      title={lineageLabel}
                      style={{
                        fontSize: Math.max(11, dashboardBodyFontSize - 2),
                        color: "#94a3b8",
                        fontWeight: 800,
                        lineHeight: 1.35,
                        marginBottom: 6,
                      }}
                    >
                      {ancestorLabels.join(" > ")}
                    </div>
                  ) : null}
                  <div style={{ fontSize: dashboardTitleFontSize, fontWeight: 800, color: "#f8fafc", lineHeight: 1.2 }}>
                    {node.label}
                  </div>
                  <div style={{ fontSize: dashboardBodyFontSize, color: "#cbd5e1", marginTop: 6 }}>
                    {node.owner || "Unassigned"} | {toTitleCase(node.frequency)} | {formatValueTypeLabel(node.valueType)}
                  </div>
                  {hasTrackedParent ? (
                    <div
                      style={{
                        fontSize: 12,
                        color: "#cbd5e1",
                        marginTop: 4,
                        fontWeight: 800,
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 8,
                        padding: "4px 10px",
                        borderRadius: 999,
                        background: "rgba(37,99,235,0.16)",
                        border: "1px solid rgba(96,165,250,0.38)",
                      }}
                    >
                      <span style={{ color: "#93c5fd", fontWeight: 700 }}>Child of</span>
                      <span>{parentLabel ?? "Parent KPI"}</span>
                    </div>
                  ) : null}
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                  <div
                    style={{
                      border: "1px solid rgba(148,163,184,0.22)",
                      borderRadius: 10,
                      padding: 14,
                      background: "rgba(8,13,20,0.86)",
                    }}
                  >
                    <div style={{ fontSize: dashboardLabelFontSize, fontWeight: 800, color: "#94a3b8", textTransform: "uppercase" }}>
                      {formatComparisonMetricLabel(comparisonMode)}
                    </div>
                    <div style={{ fontSize: dashboardMetricFontSize, fontWeight: 800, color: "#f8fafc", marginTop: 4 }}>
                      {formatMetricValue(node.target, node.valueType)}
                    </div>
                  </div>
                  <div
                    style={{
                      border: "1px solid rgba(148,163,184,0.22)",
                      borderRadius: 10,
                      padding: 14,
                      background: "rgba(8,13,20,0.86)",
                    }}
                  >
                    <div style={{ fontSize: dashboardLabelFontSize, fontWeight: 800, color: "#94a3b8", textTransform: "uppercase" }}>
                      Actual
                    </div>
                    <div style={{ fontSize: dashboardMetricFontSize, fontWeight: 800, color: "#f8fafc", marginTop: 4 }}>
                      {formatMetricValue(node.actual, node.valueType)}
                    </div>
                  </div>
                </div>

                <div
                  style={{
                    border: "1px solid rgba(148,163,184,0.22)",
                    borderRadius: 10,
                    padding: 14,
                    background: "rgba(8,13,20,0.86)",
                    display: "grid",
                    gap: 8,
                  }}
                >
                  <div style={{ fontSize: dashboardLabelFontSize, fontWeight: 800, color: "#94a3b8", textTransform: "uppercase" }}>
                    Trend
                  </div>
                  <div style={{ fontSize: 12, color: "#cbd5e1" }}>
                    {node.frequency === "weekly" ? "Rolling 4 weeks" : "Rolling 4 periods"}
                  </div>
                  <Sparkline
                    points={trendPoints}
                    color={scoreColor}
                    valueType={node.valueType}
                    width={dashboardChartWidth}
                    height={dashboardChartHeight}
                  />
                </div>

                <div
                  style={{
                    fontSize: 12,
                    color: "#94a3b8",
                    fontWeight: 700,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 12,
                    flexWrap: "wrap",
                  }}
                >
                  <span>{hasTrackedParent ? "Child KPI grouped under parent" : "Drag card to reorder dashboard"}</span>
                  {canToggleChildren ? (
                    <button
                      type="button"
                      onClick={() => toggleParentExpansion(node.id)}
                      style={{
                        border: "1px solid rgba(148,163,184,0.28)",
                        borderRadius: 999,
                        padding: "6px 10px",
                        background: "rgba(15,23,42,0.82)",
                        fontSize: 12,
                        fontWeight: 800,
                        color: "#e5e7eb",
                      }}
                    >
                      {isExpanded ? `Hide ${childCount} ${childCount === 1 ? "child" : "children"}` : `Show ${childCount} ${childCount === 1 ? "child" : "children"}`}
                    </button>
                  ) : null}
                </div>

              </div>
              </div>
            </div>
          );
        })}
            </div>
          ))}
        </div>
        <div
          onPointerDown={(event) => event.stopPropagation()}
          onWheel={(event) => event.stopPropagation()}
          style={{
            position: "absolute",
            left: 14,
            bottom: 14,
            zIndex: 5,
            display: "grid",
            border: "1px solid #d1d5db",
            borderRadius: 8,
            overflow: "hidden",
            background: "#fff",
            boxShadow: "0 10px 24px rgba(0,0,0,0.24)",
          }}
          aria-label="Dashboard canvas controls"
        >
          <button
            type="button"
            onClick={() => onDashboardZoomControl("in")}
            title="Zoom in"
            aria-label="Zoom in"
            style={{
              width: 38,
              height: 34,
              border: 0,
              borderBottom: "1px solid #e5e7eb",
              background: "#fff",
              color: "#111827",
              fontSize: 24,
              lineHeight: 1,
              cursor: "pointer",
            }}
          >
            +
          </button>
          <button
            type="button"
            onClick={() => onDashboardZoomControl("out")}
            title="Zoom out"
            aria-label="Zoom out"
            style={{
              width: 38,
              height: 34,
              border: 0,
              borderBottom: "1px solid #e5e7eb",
              background: "#fff",
              color: "#111827",
              fontSize: 26,
              lineHeight: 1,
              cursor: "pointer",
            }}
          >
            -
          </button>
          <button
            type="button"
            onClick={onDashboardFitView}
            title="Reset view"
            aria-label="Reset view"
            style={{
              width: 38,
              height: 34,
              border: 0,
              background: "#fff",
              color: "#111827",
              fontSize: 15,
              fontWeight: 900,
              lineHeight: 1,
              cursor: "pointer",
            }}
          >
            []
          </button>
        </div>
      </div>

      {editingKpiId ? (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(17, 24, 39, 0.52)",
            display: "grid",
            placeItems: "center",
            padding: 24,
            zIndex: 1000,
          }}
        >
          <div
            onClick={(event) => event.stopPropagation()}
            style={{
              width: "min(720px, 100%)",
              maxHeight: "min(82vh, 780px)",
              overflow: "auto",
              background: "#fff",
              borderRadius: 18,
              border: "1px solid #d1d5db",
              boxShadow: "0 18px 40px rgba(0,0,0,0.35)",
              padding: 22,
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                gap: 16,
                marginBottom: 18,
                position: "sticky",
                top: -22,
                margin: "-22px -22px 18px",
                padding: "22px 22px 16px",
                background: "rgba(255,255,255,0.96)",
                borderBottom: "1px solid #e5e7eb",
                zIndex: 2,
                backdropFilter: "blur(6px)",
              }}
            >
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 20, fontWeight: 800, color: "#111827" }}>
                  {selectedPersistedKpi?.data.label ?? "Edit KPI"}
                </div>
                <div style={{ fontSize: 13, color: "#6b7280", marginTop: 8 }}>
                  Changes save automatically.
                </div>
              </div>
              <button
                type="button"
                onClick={() => setEditingKpiId(null)}
                style={{
                  border: "1px solid #d1d5db",
                  borderRadius: 10,
                  background: "#fff",
                  color: "#111827",
                  padding: "8px 12px",
                  fontSize: 13,
                  fontWeight: 700,
                  cursor: "pointer",
                }}
              >
                Close
              </button>
            </div>

            {!selectedPersistedKpi ? null : (
              <div style={{ display: "grid", gap: 18 }}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                  <label style={{ display: "grid", gap: 6 }}>
                    <span style={{ fontSize: 12, fontWeight: 800, color: "#6b7280" }}>KPI Name</span>
                    <input
                      value={selectedPersistedKpi.data.label ?? ""}
                      onChange={(event) => onKpiFieldChange(selectedPersistedKpi.id, "label", event.target.value)}
                      style={{ border: "1px solid #d1d5db", borderRadius: 10, padding: "10px 12px", fontSize: 14 }}
                    />
                  </label>
                  <label style={{ display: "grid", gap: 6 }}>
                    <span style={{ fontSize: 12, fontWeight: 800, color: "#6b7280" }}>Owner</span>
                    <select
                      value={ownerOptions.includes(selectedPersistedKpi.data.owner ?? "") ? (selectedPersistedKpi.data.owner ?? "") : ""}
                      onChange={(event) => onKpiFieldChange(selectedPersistedKpi.id, "owner", event.target.value)}
                      style={{ border: "1px solid #d1d5db", borderRadius: 10, padding: "10px 12px", fontSize: 14, background: "#fff" }}
                    >
                      <option value="">Select owner</option>
                      {ownerOptions.map((owner) => (
                        <option key={owner} value={owner}>{owner}</option>
                      ))}
                    </select>
                  </label>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 14 }}>
                  <label style={{ display: "grid", gap: 6 }}>
                    <span style={{ fontSize: 12, fontWeight: 800, color: "#6b7280" }}>Frequency</span>
                    <select
                      value={selectedPersistedKpi.data.frequency ?? "weekly"}
                      onChange={(event) => onKpiDetailChange(selectedPersistedKpi.id, "frequency", event.target.value)}
                      style={{ border: "1px solid #d1d5db", borderRadius: 10, padding: "10px 12px", fontSize: 14, background: "#fff" }}
                    >
                      {frequencyOptions.map((option) => <option key={option} value={option}>{formatEnumLabel(option)}</option>)}
                    </select>
                  </label>
                  <label style={{ display: "grid", gap: 6 }}>
                    <span style={{ fontSize: 12, fontWeight: 800, color: "#6b7280" }}>Data Source</span>
                    <select
                      value={dataSourceOptions.includes((selectedPersistedKpi.data.dataSource ?? "Manual") as (typeof dataSourceOptions)[number]) ? (selectedPersistedKpi.data.dataSource ?? "Manual") : "Manual"}
                      onChange={(event) => onKpiDetailChange(selectedPersistedKpi.id, "dataSource", event.target.value)}
                      style={{ border: "1px solid #d1d5db", borderRadius: 10, padding: "10px 12px", fontSize: 14, background: "#fff" }}
                    >
                      {dataSourceOptions.map((option) => <option key={option} value={option}>{option}</option>)}
                    </select>
                  </label>
                  <label style={{ display: "grid", gap: 6 }}>
                    <span style={{ fontSize: 12, fontWeight: 800, color: "#6b7280" }}>Value Type</span>
                    <select
                      value={
                        normalizeCalculationMode(
                          selectedPersistedKpi.data.calculationMode,
                          selectedPersistedKpi.data.isRollup
                        ) === "formula_percentage"
                          ? "percentage"
                          : selectedPersistedKpi.data.dataSource === "Poll"
                            ? "rating_1_10"
                            : (selectedPersistedKpi.data.valueType ?? "count")
                      }
                      onChange={(event) => onKpiDetailChange(selectedPersistedKpi.id, "valueType", event.target.value)}
                      disabled={
                        selectedPersistedKpi.data.dataSource === "Poll" ||
                        normalizeCalculationMode(
                          selectedPersistedKpi.data.calculationMode,
                          selectedPersistedKpi.data.isRollup
                        ) === "formula_percentage"
                      }
                      style={{ border: "1px solid #d1d5db", borderRadius: 10, padding: "10px 12px", fontSize: 14, background: selectedPersistedKpi.data.dataSource === "Poll" || normalizeCalculationMode(selectedPersistedKpi.data.calculationMode, selectedPersistedKpi.data.isRollup) === "formula_percentage" ? "#f3f4f6" : "#fff" }}
                    >
                      {valueTypeOptions.map((option) => <option key={option} value={option}>{formatValueTypeLabel(option)}</option>)}
                    </select>
                  </label>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                  <label style={{ display: "grid", gap: 6 }}>
                    <span style={{ fontSize: 12, fontWeight: 800, color: "#6b7280" }}>Performance Direction</span>
                    <select
                      value={selectedPersistedKpi.data.performanceDirection ?? "higher_is_better"}
                      onChange={(event) => onKpiConfigChange(selectedPersistedKpi.id, "performanceDirection", event.target.value)}
                      style={{ border: "1px solid #d1d5db", borderRadius: 10, padding: "10px 12px", fontSize: 14, background: "#fff" }}
                    >
                      <option value="higher_is_better">Higher is better</option>
                      <option value="lower_is_better">Lower is better</option>
                    </select>
                  </label>
                  <label style={{ display: "grid", gap: 6 }}>
                    <span style={{ fontSize: 12, fontWeight: 800, color: "#6b7280" }}>KPI Calculation</span>
                    <select
                      value={normalizeCalculationMode(selectedPersistedKpi.data.calculationMode, selectedPersistedKpi.data.isRollup)}
                      onChange={(event) => onKpiConfigChange(selectedPersistedKpi.id, "calculationMode", event.target.value)}
                      style={{ border: "1px solid #d1d5db", borderRadius: 10, padding: "10px 12px", fontSize: 14, background: "#fff" }}
                    >
                      {calculationModeOptions.map((option) => <option key={option} value={option}>{formatCalculationModeLabel(option)}</option>)}
                    </select>
                  </label>
                </div>

                {normalizeCalculationMode(
                  selectedPersistedKpi.data.calculationMode,
                  selectedPersistedKpi.data.isRollup
                ) === "formula_percentage" ? (
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                    <label style={{ display: "grid", gap: 6 }}>
                      <span style={{ fontSize: 12, fontWeight: 800, color: "#6b7280" }}>Numerator KPI</span>
                      <select
                        value={selectedPersistedKpi.data.formulaNumeratorKpiId ?? ""}
                        onChange={(event) => onKpiDetailChange(selectedPersistedKpi.id, "formulaNumeratorKpiId", event.target.value)}
                        style={{ border: "1px solid #d1d5db", borderRadius: 10, padding: "10px 12px", fontSize: 14, background: "#fff" }}
                      >
                        <option value="">Select numerator KPI</option>
                        {(workspace?.allNodes ?? [])
                          .filter((node) => node.id !== selectedPersistedKpi.id)
                          .map((node) => <option key={node.id} value={node.id}>{node.data.label}</option>)}
                      </select>
                    </label>
                    <label style={{ display: "grid", gap: 6 }}>
                      <span style={{ fontSize: 12, fontWeight: 800, color: "#6b7280" }}>Denominator KPI</span>
                      <select
                        value={selectedPersistedKpi.data.formulaDenominatorKpiId ?? ""}
                        onChange={(event) => onKpiDetailChange(selectedPersistedKpi.id, "formulaDenominatorKpiId", event.target.value)}
                        style={{ border: "1px solid #d1d5db", borderRadius: 10, padding: "10px 12px", fontSize: 14, background: "#fff" }}
                      >
                        <option value="">Select denominator KPI</option>
                        {(workspace?.allNodes ?? [])
                          .filter((node) => node.id !== selectedPersistedKpi.id)
                          .map((node) => <option key={node.id} value={node.id}>{node.data.label}</option>)}
                      </select>
                    </label>
                  </div>
                ) : null}

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14 }}>
                  <label style={{ display: "grid", gap: 6 }}>
                    <span style={{ fontSize: 12, fontWeight: 800, color: "#6b7280" }}>Event Date</span>
                    <input
                      type="date"
                      value={selectedPersistedKpi.data.eventDate ?? ""}
                      onChange={(event) => onKpiDetailChange(selectedPersistedKpi.id, "eventDate", event.target.value)}
                      style={{ border: "1px solid #d1d5db", borderRadius: 10, padding: "10px 12px", fontSize: 14 }}
                    />
                  </label>
                  <label style={{ display: "grid", gap: 6 }}>
                    <span style={{ fontSize: 12, fontWeight: 800, color: "#6b7280" }}>{formatComparisonMetricLabel(comparisonMode)}</span>
                    <input
                      type="number"
                      min={getNormalizedValueType(selectedPersistedKpi.data.dataSource, selectedPersistedKpi.data.valueType) === "rating_1_10" ? 1 : undefined}
                      max={getNormalizedValueType(selectedPersistedKpi.data.dataSource, selectedPersistedKpi.data.valueType) === "rating_1_10" ? 10 : undefined}
                      step={getNormalizedValueType(selectedPersistedKpi.data.dataSource, selectedPersistedKpi.data.valueType) === "rating_1_10" ? 1 : "any"}
                      value={
                        normalizeCalculationMode(
                          selectedPersistedKpi.data.calculationMode,
                          selectedPersistedKpi.data.isRollup
                        ) === "calculated_values" ||
                        normalizeCalculationMode(
                          selectedPersistedKpi.data.calculationMode,
                          selectedPersistedKpi.data.isRollup
                        ) === "formula_percentage"
                          ? (dashboardNodes.find((node) => node.id === selectedPersistedKpi.id)?.target ?? "")
                          : typeof selectedPersistedKpi.data.targetHistory?.[getMetricKey(referenceDate, selectedPersistedKpi.data.frequency ?? "weekly", "global", defaultCampus)] === "number"
                            ? selectedPersistedKpi.data.targetHistory?.[getMetricKey(referenceDate, selectedPersistedKpi.data.frequency ?? "weekly", "global", defaultCampus)]
                            : ""
                      }
                      onChange={(event) => onKpiMetricChange(selectedPersistedKpi.id, "target", event.target.value)}
                      disabled={normalizeCalculationMode(selectedPersistedKpi.data.calculationMode, selectedPersistedKpi.data.isRollup) === "calculated_values"}
                      style={{ border: "1px solid #d1d5db", borderRadius: 10, padding: "10px 12px", fontSize: 14, background: normalizeCalculationMode(selectedPersistedKpi.data.calculationMode, selectedPersistedKpi.data.isRollup) === "calculated_values" ? "#f3f4f6" : "#fff" }}
                    />
                  </label>
                  <label style={{ display: "grid", gap: 6 }}>
                    <span style={{ fontSize: 12, fontWeight: 800, color: "#6b7280" }}>Actual</span>
                    <input
                      type="number"
                      min={getNormalizedValueType(selectedPersistedKpi.data.dataSource, selectedPersistedKpi.data.valueType) === "rating_1_10" ? 1 : undefined}
                      max={getNormalizedValueType(selectedPersistedKpi.data.dataSource, selectedPersistedKpi.data.valueType) === "rating_1_10" ? 10 : undefined}
                      step={getNormalizedValueType(selectedPersistedKpi.data.dataSource, selectedPersistedKpi.data.valueType) === "rating_1_10" ? 1 : "any"}
                      value={
                        normalizeCalculationMode(
                          selectedPersistedKpi.data.calculationMode,
                          selectedPersistedKpi.data.isRollup
                        ) === "calculated_values" ||
                        normalizeCalculationMode(
                          selectedPersistedKpi.data.calculationMode,
                          selectedPersistedKpi.data.isRollup
                        ) === "formula_percentage"
                          ? (dashboardNodes.find((node) => node.id === selectedPersistedKpi.id)?.actual ?? "")
                          : typeof selectedPersistedKpi.data.actualHistory?.[getMetricKey(referenceDate, selectedPersistedKpi.data.frequency ?? "weekly", "global", defaultCampus)] === "number"
                            ? selectedPersistedKpi.data.actualHistory?.[getMetricKey(referenceDate, selectedPersistedKpi.data.frequency ?? "weekly", "global", defaultCampus)]
                            : ""
                      }
                      onChange={(event) => onKpiMetricChange(selectedPersistedKpi.id, "actual", event.target.value)}
                      disabled={normalizeCalculationMode(selectedPersistedKpi.data.calculationMode, selectedPersistedKpi.data.isRollup) === "calculated_values" || normalizeCalculationMode(selectedPersistedKpi.data.calculationMode, selectedPersistedKpi.data.isRollup) === "formula_percentage" || selectedPersistedKpi.data.dataSource === "Poll"}
                      style={{ border: "1px solid #d1d5db", borderRadius: 10, padding: "10px 12px", fontSize: 14, background: normalizeCalculationMode(selectedPersistedKpi.data.calculationMode, selectedPersistedKpi.data.isRollup) === "calculated_values" || normalizeCalculationMode(selectedPersistedKpi.data.calculationMode, selectedPersistedKpi.data.isRollup) === "formula_percentage" || selectedPersistedKpi.data.dataSource === "Poll" ? "#f3f4f6" : "#fff" }}
                    />
                  </label>
                </div>

                <label style={{ display: "grid", gap: 6 }}>
                  <span style={{ fontSize: 12, fontWeight: 800, color: "#6b7280" }}>Definition</span>
                  <textarea
                    value={selectedPersistedKpi.data.definition ?? ""}
                    onChange={(event) => onKpiDetailChange(selectedPersistedKpi.id, "definition", event.target.value)}
                    rows={3}
                    style={{ border: "1px solid #d1d5db", borderRadius: 10, padding: "10px 12px", fontSize: 14, resize: "vertical" }}
                  />
                </label>
                <label style={{ display: "grid", gap: 6 }}>
                  <span style={{ fontSize: 12, fontWeight: 800, color: "#6b7280" }}>Measurement</span>
                  <textarea
                    value={selectedPersistedKpi.data.measurement ?? ""}
                    onChange={(event) => onKpiDetailChange(selectedPersistedKpi.id, "measurement", event.target.value)}
                    rows={3}
                    style={{ border: "1px solid #d1d5db", borderRadius: 10, padding: "10px 12px", fontSize: 14, resize: "vertical" }}
                  />
                </label>

                <div
                  style={{
                    border: "1px solid #e5e7eb",
                    borderRadius: 14,
                    background: "#fff",
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: 12,
                      padding: "14px 16px",
                      background: "#f9fafb",
                      color: "#111827",
                      flexWrap: "wrap",
                    }}
                  >
                    <div style={{ textAlign: "left" }}>
                      <div style={{ fontSize: 16, fontWeight: 800 }}>KPI Polling</div>
                      <div style={{ fontSize: 12, color: "#4b5563", marginTop: 2 }}>
                        Create and review poll-based KPI responses
                      </div>
                    </div>
                    {!selectedPersistedKpi.data.pollConfig ? (
                      <button
                        type="button"
                        onClick={() => onCreatePoll(selectedPersistedKpi.id)}
                        style={{
                          border: "1px solid #d1d5db",
                          borderRadius: 10,
                          padding: "8px 12px",
                          background: "#fff",
                          fontWeight: 700,
                          cursor: "pointer",
                        }}
                      >
                        Create Poll
                      </button>
                    ) : (
                      <Link
                        to={selectedEnterpriseId ? `/?enterpriseId=${selectedEnterpriseId}` : "/"}
                        style={{
                          border: "1px solid #bfdbfe",
                          borderRadius: 10,
                          padding: "8px 12px",
                          background: "#eff6ff",
                          color: "#1d4ed8",
                          fontWeight: 800,
                          textDecoration: "none",
                        }}
                      >
                        Manage Poll
                      </Link>
                    )}
                  </div>

                  {selectedPersistedKpi.data.pollConfig ? (
                    <div
                      style={{
                        padding: 16,
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: 12,
                        flexWrap: "wrap",
                      }}
                    >
                      <div style={{ display: "grid", gap: 4 }}>
                        <div style={{ fontSize: 14, fontWeight: 800 }}>
                          {selectedPersistedKpi.data.pollConfig.title}
                        </div>
                        <div style={{ fontSize: 13, color: "#6b7280" }}>
                          {selectedPersistedKpi.data.pollConfig.isAnonymous ? "Anonymous" : "Identified"} rating poll for{" "}
                          {(selectedPersistedKpi.data.pollConfig.targetGroups?.length ?? 0) > 0
                            ? selectedPersistedKpi.data.pollConfig.targetGroups.join(", ")
                            : selectedPersistedKpi.data.pollConfig.targetGroup || "selected groups"}
                        </div>
                      </div>
                      <div
                        style={{
                          fontSize: 12,
                          fontWeight: 800,
                          color:
                            selectedPersistedKpi.data.pollConfig.status === "published"
                              ? "#166534"
                              : "#92400e",
                          textTransform: "uppercase",
                          letterSpacing: "0.08em",
                        }}
                      >
                        {selectedPersistedKpi.data.pollConfig.status}
                      </div>
                    </div>
                  ) : null}
                </div>

                <div
                  style={{
                    border: "1px solid #e5e7eb",
                    borderRadius: 14,
                    overflow: "hidden",
                    background: "#fff",
                  }}
                >
                  <button
                    type="button"
                    onClick={() => setIsKpiWorkOpen((current) => !current)}
                    style={{
                      width: "100%",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: 12,
                      padding: "14px 16px",
                      border: "none",
                      background: "#f9fafb",
                      color: "#111827",
                      cursor: "pointer",
                    }}
                  >
                    <div style={{ textAlign: "left" }}>
                      <div style={{ fontSize: 16, fontWeight: 800 }}>
                        KPI Initiatives and Tasks
                      </div>
                      <div style={{ fontSize: 12, color: "#4b5563", marginTop: 2 }}>
                        Manage direct KPI tasks and initiative-based work
                      </div>
                    </div>
                    <div style={{ fontSize: 12, fontWeight: 800, color: "#2563eb" }}>
                      {isKpiWorkOpen ? "Hide" : "Show"}
                    </div>
                  </button>

                  {isKpiWorkOpen ? (
                    <div style={{ padding: 16, display: "grid", gap: 18 }}>
                      <div style={{ display: "grid", gap: 12 }}>
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            gap: 12,
                            flexWrap: "wrap",
                          }}
                        >
                          <div style={{ fontSize: 16, fontWeight: 800 }}>Initiatives</div>
                          <button
                            type="button"
                            onClick={() => onAddInitiative(selectedPersistedKpi.id)}
                            style={{
                              border: "1px solid #d1d5db",
                              borderRadius: 10,
                              padding: "8px 10px",
                              background: "#fff",
                              fontWeight: 700,
                              cursor: "pointer",
                            }}
                          >
                            Add Initiative
                          </button>
                        </div>
                        {(selectedPersistedKpi.data.initiatives ?? []).length === 0 ? (
                          <div style={{ color: "#6b7280", fontSize: 13 }}>No initiatives yet.</div>
                        ) : (
                          (selectedPersistedKpi.data.initiatives ?? []).map((initiative) => (
                            <div
                              key={initiative.id}
                              style={{
                                border: "1px solid #d1d5db",
                                borderRadius: 16,
                                padding: 18,
                                display: "grid",
                                gap: 14,
                                background: "#fff",
                                boxShadow: "0 10px 24px rgba(15, 23, 42, 0.06)",
                              }}
                            >
                              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                                <button
                                  type="button"
                                  onClick={() => onDeleteInitiative(selectedPersistedKpi.id, initiative.id)}
                                  style={{
                                    width: 28,
                                    height: 28,
                                    border: "1px solid transparent",
                                    borderRadius: 999,
                                    padding: 0,
                                    background: "transparent",
                                    color: "#9ca3af",
                                    fontWeight: 700,
                                    fontSize: 18,
                                    lineHeight: 1,
                                    cursor: "pointer",
                                  }}
                                  title="Delete initiative"
                                >
                                  ×
                                </button>
                              </div>
                              <input
                                value={initiative.title}
                                onChange={(event) =>
                                  onInitiativeChange(
                                    selectedPersistedKpi.id,
                                    initiative.id,
                                    "title",
                                    event.target.value
                                  )
                                }
                                style={{ border: "1px solid transparent", borderRadius: 8, padding: "4px 0", fontSize: 18, fontWeight: 800, background: "transparent", color: "#111827" }}
                              />
                              <div style={{ display: "grid", gridTemplateColumns: "minmax(120px, 1fr) repeat(4, minmax(96px, auto))", gap: 8, alignItems: "center", color: "#6b7280", fontSize: 12 }}>
                                <select
                                  value={ownerOptions.includes(initiative.owner) ? initiative.owner : ""}
                                  onChange={(event) =>
                                    onInitiativeChange(
                                      selectedPersistedKpi.id,
                                      initiative.id,
                                      "owner",
                                      event.target.value
                                    )
                                  }
                                  style={{ border: "none", borderBottom: "1px solid #e5e7eb", padding: "4px 0", fontSize: 12, fontWeight: 700, background: "transparent", color: "#4b5563", minWidth: 0 }}
                                >
                                  <option value="">Unassigned</option>
                                  {ownerOptions.map((owner) => (
                                    <option key={owner} value={owner}>{owner}</option>
                                  ))}
                                </select>
                                <input
                                  type="date"
                                  value={initiative.dueDate}
                                  onChange={(event) =>
                                    onInitiativeChange(
                                      selectedPersistedKpi.id,
                                      initiative.id,
                                      "dueDate",
                                      event.target.value
                                    )
                                  }
                                  title={formatWorkDueDate(initiative.dueDate)}
                                  style={{ border: "none", borderBottom: "1px solid #e5e7eb", padding: "4px 0", fontSize: 12, fontWeight: 700, background: "transparent", color: "#6b7280", minWidth: 0 }}
                                />
                                <select
                                  value={initiative.status}
                                  onChange={(event) =>
                                    onInitiativeChange(
                                      selectedPersistedKpi.id,
                                      initiative.id,
                                      "status",
                                      event.target.value
                                    )
                                  }
                                  style={{ ...workStatusChipStyle(initiative.status), appearance: "none", cursor: "pointer", minWidth: 0 }}
                                >
                                  {workStatusOptions.map((status) => (
                                    <option key={status} value={status}>{formatWorkStatusLabel(status)}</option>
                                  ))}
                                </select>
                                <select
                                  value={initiative.priority ?? "normal"}
                                  onChange={(event) =>
                                    onInitiativeChange(
                                      selectedPersistedKpi.id,
                                      initiative.id,
                                      "priority",
                                      event.target.value
                                    )
                                  }
                                  style={{ ...workPriorityChipStyle(initiative.priority), appearance: "none", cursor: "pointer", minWidth: 0 }}
                                >
                                  {workPriorityOptions.map((priority) => (
                                    <option key={priority} value={priority}>{formatWorkPriorityLabel(priority)}</option>
                                  ))}
                                </select>
                                <select
                                  value={initiative.effort ?? "medium"}
                                  onChange={(event) =>
                                    onInitiativeChange(
                                      selectedPersistedKpi.id,
                                      initiative.id,
                                      "effort",
                                      event.target.value
                                    )
                                  }
                                  style={{ ...workEffortChipStyle(initiative.effort), appearance: "none", cursor: "pointer", minWidth: 0 }}
                                >
                                  {workEffortOptions.map((effort) => (
                                    <option key={effort} value={effort}>{formatWorkEffortLabel(effort)}</option>
                                  ))}
                                </select>
                              </div>
                              <div style={{ display: "grid", gap: 6 }}>
                                <div style={{ fontSize: 11, fontWeight: 800, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                                  Description
                                </div>
                                <textarea
                                  value={initiative.summary}
                                  placeholder="Describe what this initiative is meant to accomplish"
                                  onChange={(event) =>
                                    onInitiativeChange(
                                      selectedPersistedKpi.id,
                                      initiative.id,
                                      "summary",
                                      event.target.value
                                    )
                                  }
                                  rows={3}
                                  style={{ border: "1px solid #e5e7eb", borderRadius: 10, padding: "10px 12px", fontSize: 14, resize: "vertical", background: "#f9fafb", color: "#374151", lineHeight: 1.45 }}
                                />
                              </div>
                              <div style={{ display: "grid", gap: 8 }}>
                                <div
                                  style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    gap: 12,
                                    flexWrap: "wrap",
                                  }}
                                >
                                  <div style={{ fontSize: 13, fontWeight: 800, color: "#6b7280" }}>
                                    Initiative Tasks
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => onAddInitiativeTask(selectedPersistedKpi.id, initiative.id)}
                                    style={{
                                      border: "1px solid #d1d5db",
                                      borderRadius: 10,
                                      padding: "6px 8px",
                                      background: "#fff",
                                      fontWeight: 700,
                                      cursor: "pointer",
                                    }}
                                  >
                                    Add Task
                                  </button>
                                </div>
                                {(initiative.tasks ?? []).map((task) => (
                                  <div
                                    key={task.id}
                                    style={{
                                      border: "1px solid #e5e7eb",
                                      borderRadius: 12,
                                      padding: 12,
                                      display: "grid",
                                      gap: 8,
                                      background: "#f9fafb",
                                    }}
                                  >
                                    <div style={{ display: "flex", justifyContent: "flex-end" }}>
                                      <button
                                        type="button"
                                        onClick={() =>
                                          onDeleteInitiativeTask(
                                            selectedPersistedKpi.id,
                                            initiative.id,
                                            task.id
                                          )
                                        }
                                        style={{
                                          width: 26,
                                          height: 26,
                                          border: "1px solid transparent",
                                          borderRadius: 999,
                                          padding: 0,
                                          background: "transparent",
                                          color: "#9ca3af",
                                          fontWeight: 700,
                                          fontSize: 16,
                                          lineHeight: 1,
                                          cursor: "pointer",
                                        }}
                                        title="Delete task"
                                      >
                                        ×
                                      </button>
                                    </div>
                                    <input
                                      value={task.title}
                                      onChange={(event) =>
                                        onInitiativeTaskChange(
                                          selectedPersistedKpi.id,
                                          initiative.id,
                                          task.id,
                                          "title",
                                          event.target.value
                                        )
                                      }
                                      style={{ border: "1px solid transparent", borderRadius: 8, padding: "6px 8px", fontSize: 13, fontWeight: 700, background: "#fff", color: "#111827" }}
                                    />
                                    <div style={{ display: "grid", gridTemplateColumns: "minmax(120px, 1fr) repeat(4, minmax(92px, auto))", gap: 8 }}>
                                      <select
                                        value={ownerOptions.includes(task.owner) ? task.owner : ""}
                                        onChange={(event) =>
                                          onInitiativeTaskChange(
                                            selectedPersistedKpi.id,
                                            initiative.id,
                                            task.id,
                                            "owner",
                                            event.target.value
                                          )
                                        }
                                        style={{ border: "1px solid #e5e7eb", borderRadius: 10, padding: "8px 10px", fontSize: 12, background: "#fff", minWidth: 0 }}
                                      >
                                        <option value="">Unassigned</option>
                                        {ownerOptions.map((owner) => (
                                          <option key={owner} value={owner}>{owner}</option>
                                        ))}
                                      </select>
                                      <input
                                        type="date"
                                        value={task.dueDate}
                                        onChange={(event) =>
                                          onInitiativeTaskChange(
                                            selectedPersistedKpi.id,
                                            initiative.id,
                                            task.id,
                                            "dueDate",
                                            event.target.value
                                          )
                                        }
                                        style={{ border: "1px solid #e5e7eb", borderRadius: 10, padding: "8px 10px", fontSize: 12, minWidth: 0, background: "#fff" }}
                                      />
                                      <select
                                        value={task.status}
                                        onChange={(event) =>
                                          onInitiativeTaskChange(
                                            selectedPersistedKpi.id,
                                            initiative.id,
                                            task.id,
                                            "status",
                                            event.target.value
                                          )
                                        }
                                        style={{ border: "1px solid #e5e7eb", borderRadius: 10, padding: "8px 10px", fontSize: 12, background: "#fff", minWidth: 0 }}
                                      >
                                        {workStatusOptions.map((status) => (
                                          <option key={status} value={status}>{formatWorkStatusLabel(status)}</option>
                                        ))}
                                      </select>
                                      <select
                                        value={task.priority ?? "normal"}
                                        onChange={(event) =>
                                          onInitiativeTaskChange(
                                            selectedPersistedKpi.id,
                                            initiative.id,
                                            task.id,
                                            "priority",
                                            event.target.value
                                          )
                                        }
                                        style={{ ...workPriorityChipStyle(task.priority), appearance: "none", cursor: "pointer", minWidth: 0 }}
                                      >
                                        {workPriorityOptions.map((priority) => (
                                          <option key={priority} value={priority}>{formatWorkPriorityLabel(priority)}</option>
                                        ))}
                                      </select>
                                      <select
                                        value={task.effort ?? "medium"}
                                        onChange={(event) =>
                                          onInitiativeTaskChange(
                                            selectedPersistedKpi.id,
                                            initiative.id,
                                            task.id,
                                            "effort",
                                            event.target.value
                                          )
                                        }
                                        style={{ ...workEffortChipStyle(task.effort), appearance: "none", cursor: "pointer", minWidth: 0 }}
                                      >
                                        {workEffortOptions.map((effort) => (
                                          <option key={effort} value={effort}>{formatWorkEffortLabel(effort)}</option>
                                        ))}
                                      </select>
                                    </div>
                                    <textarea
                                      value={task.notes}
                                      placeholder="Notes"
                                      onChange={(event) =>
                                        onInitiativeTaskChange(
                                          selectedPersistedKpi.id,
                                          initiative.id,
                                          task.id,
                                          "notes",
                                          event.target.value
                                        )
                                      }
                                      rows={2}
                                      style={{ border: "1px solid #e5e7eb", borderRadius: 10, padding: "8px 10px", fontSize: 13, resize: "vertical", background: "#fff" }}
                                    />
                                  </div>
                                ))}
                              </div>
                            </div>
                          ))
                        )}
                      </div>

                      <div style={{ display: "grid", gap: 12 }}>
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            gap: 12,
                            flexWrap: "wrap",
                          }}
                        >
                          <div style={{ fontSize: 16, fontWeight: 800 }}>KPI Tasks</div>
                          <button
                            type="button"
                            onClick={() => onAddTask(selectedPersistedKpi.id)}
                            style={{
                              border: "1px solid #d1d5db",
                              borderRadius: 10,
                              padding: "8px 10px",
                              background: "#fff",
                              fontWeight: 700,
                              cursor: "pointer",
                            }}
                          >
                            Add Task
                          </button>
                        </div>
                        {(selectedPersistedKpi.data.tasks ?? []).length === 0 ? (
                          <div style={{ color: "#6b7280", fontSize: 13 }}>No direct KPI tasks yet.</div>
                        ) : (
                          (selectedPersistedKpi.data.tasks ?? []).map((task) => (
                            <div
                              key={task.id}
                              style={{
                                border: "1px solid #e5e7eb",
                                borderRadius: 14,
                                padding: 12,
                                display: "grid",
                                gap: 8,
                                background: "#fff",
                              }}
                            >
                              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                                <button
                                  type="button"
                                  onClick={() => onDeleteTask(selectedPersistedKpi.id, task.id)}
                                  style={{
                                    border: "1px solid #fecaca",
                                    borderRadius: 10,
                                    padding: "6px 10px",
                                    background: "#fff5f5",
                                    color: "#b91c1c",
                                    fontWeight: 700,
                                    cursor: "pointer",
                                  }}
                                >
                                  Delete Task
                                </button>
                              </div>
                              <input
                                value={task.title}
                                onChange={(event) =>
                                  onTaskChange(
                                    selectedPersistedKpi.id,
                                    task.id,
                                    "title",
                                    event.target.value
                                  )
                                }
                                style={{ border: "1px solid #d1d5db", borderRadius: 10, padding: "10px 12px", fontSize: 14 }}
                              />
                              <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) 150px 150px", gap: 8 }}>
                                <select
                                  value={ownerOptions.includes(task.owner) ? task.owner : ""}
                                  onChange={(event) =>
                                    onTaskChange(
                                      selectedPersistedKpi.id,
                                      task.id,
                                      "owner",
                                      event.target.value
                                    )
                                  }
                                  style={{ border: "1px solid #d1d5db", borderRadius: 10, padding: "10px 12px", fontSize: 14, background: "#fff", minWidth: 0 }}
                                >
                                  <option value="">Unassigned</option>
                                  {ownerOptions.map((owner) => (
                                    <option key={owner} value={owner}>{owner}</option>
                                  ))}
                                </select>
                                <input
                                  type="date"
                                  value={task.dueDate}
                                  onChange={(event) =>
                                    onTaskChange(
                                      selectedPersistedKpi.id,
                                      task.id,
                                      "dueDate",
                                      event.target.value
                                    )
                                  }
                                  style={{ border: "1px solid #d1d5db", borderRadius: 10, padding: "10px 12px", fontSize: 14, minWidth: 0 }}
                                />
                                <select
                                  value={task.status}
                                  onChange={(event) =>
                                    onTaskChange(
                                      selectedPersistedKpi.id,
                                      task.id,
                                      "status",
                                      event.target.value
                                    )
                                  }
                                  style={{ border: "1px solid #d1d5db", borderRadius: 10, padding: "10px 12px", fontSize: 14, background: "#fff", minWidth: 0 }}
                                >
                                  {workStatusOptions.map((status) => (
                                    <option key={status} value={status}>{formatEnumLabel(status)}</option>
                                  ))}
                                </select>
                              </div>
                              <textarea
                                value={task.notes}
                                placeholder="Notes"
                                onChange={(event) =>
                                  onTaskChange(
                                    selectedPersistedKpi.id,
                                    task.id,
                                    "notes",
                                    event.target.value
                                  )
                                }
                                rows={3}
                                style={{ border: "1px solid #d1d5db", borderRadius: 10, padding: "10px 12px", fontSize: 14, resize: "vertical" }}
                              />
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>
            )}
          </div>
        </div>
      ) : null}

      {!isLoading && dashboardCards.length === 0 ? (
        <div
          style={{
            marginTop: 18,
            background: "rgba(255,255,255,0.96)",
            borderRadius: 18,
            border: "1px solid #d1d5db",
            padding: 18,
            color: "#4b5563",
          }}
        >
          No KPIs are on this dashboard yet. Open Settings to add some.
        </div>
      ) : null}

      <div style={{ marginTop: 18, fontSize: 12, color: "#9ca3af" }}>
        {isSaving ? "Saving dashboard..." : "Dashboard changes save automatically."}
      </div>
    </div>
  );
}
